import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';

// Importamos la conexión con Google Firebase
import { auth, db } from './firebase'; 
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut 
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, addDoc, deleteDoc, writeBatch, onSnapshot } from "firebase/firestore";

// ----------------- 1. ESTILOS GLOBALES Y TOAST (DIETA VISUAL APLICADA SOLO A MÁRGENES) -----------------
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500&family=Nunito:wght@300;400;600;700;800&display=swap');
    * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; font-family: 'Nunito', sans-serif; }
    body { margin: 0; background-color: #F8FAFC; color: #1E293B; -webkit-font-smoothing: antialiased; }
    textarea, input, select { transition: all 0.2s ease; border-radius: 10px; border: 2px solid #CBD5E1; padding: 10px 12px; font-size: 16px; width: 100%; font-weight: 500; background-color: #ffffff; }
    textarea:focus, input:focus, select:focus { outline: none; border-color: #94A3B8; box-shadow: 0 0 0 3px rgba(226, 232, 240, 0.5); }
    button { transition: transform 0.1s ease; border: none; cursor: pointer; background: none; }
    button:active { transform: scale(0.96); }
    .auth-container { max-width: 400px; margin: 30px auto; padding: 20px; background: white; border-radius: 24px; box-shadow: 0 10px 25px rgba(0,0,0,0.06); text-align: center; }
    .admin-btn { background: #1E293B; color: white; padding: 10px 20px; border-radius: 10px; font-weight: bold; margin-bottom: 15px; width: 100%; font-size: 16px; }
    .cita-box { background: #E0E7FF; border: 2px solid #6366F1; border-radius: 12px; padding: 10px; margin-bottom: 15px; text-align: center; }
    @keyframes popIn { 0% { transform: translate(-50%, -20px); opacity: 0; } 100% { transform: translate(-50%, 0); opacity: 1; } }
  `}</style>
);

const ToastExito = ({ mostrar }) => {
  if (!mostrar) return null;
  return (
    <div style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#10B981', color: 'white', padding: '12px 25px', borderRadius: '50px', fontSize: '16px', fontWeight: '800', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', zIndex: 9999, animation: 'popIn 0.3s ease-out', textAlign: 'center', width: 'max-content', maxWidth: '90%' }}>
      ¡Buen trabajo! Sigue así 😊🌟
    </div>
  );
};

// ----------------- 2. COMPONENTES DE APOYO -----------------
const AutoTextarea = ({ value, onChange, placeholder, style, onKeyDown }) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px'; 
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea 
      ref={textareaRef} 
      value={value} 
      onChange={onChange} 
      onKeyDown={onKeyDown} 
      placeholder={placeholder} 
      style={{ minHeight: '44px', overflow: 'hidden', resize: 'none', ...style }} 
    />
  );
};

const ConductaInput = ({ onAdd, customPlaceholder }) => {
  const [val, setVal] = useState("");
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '10px' }}>
      <AutoTextarea value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e => { if(e.key === 'Enter' && !e.shiftKey && val.trim()) { e.preventDefault(); onAdd(val); setVal(""); } }} placeholder={customPlaceholder || "Añadir conducta..."} style={{ flex: 1, marginBottom: 0 }} />
      <button onClick={()=>{if(val.trim()){onAdd(val); setVal("");}}} style={{ backgroundColor: '#1E293B', color: 'white', padding: '12px', borderRadius: '10px', fontWeight: 'bold' }}>+</button>
    </div>
  );
};

// ----------------- MODAL DE PRIVACIDAD -----------------
const ModalPrivacidad = ({ onClose }) => (
  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px' }}>
    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '24px', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
      <h2 style={{ color: '#1E3A8A', marginTop: 0, marginBottom: '20px', fontSize: '22px', textAlign: 'center' }}>Política de Privacidad y Protección de Datos</h2>
      <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', textAlign: 'justify' }}>
        <p><strong>1. Responsable del Tratamiento:</strong> Francisco Beltrán (Psicólogo). Puedes contactar en psicologofranciscobeltran@hotmail.com.</p>
        <p><strong>2. Finalidad:</strong> Los datos recogidos en esta aplicación se utilizan exclusivamente para el seguimiento clínico y terapéutico del paciente, facilitando herramientas de autoconocimiento y registro entre sesiones.</p>
        <p><strong>3. Legitimación:</strong> El tratamiento de datos se basa en tu consentimiento expreso al marcar la casilla de aceptación y en la prestación de servicios de asistencia psicológica.</p>
        <p><strong>4. Almacenamiento y Seguridad:</strong> Tus datos están alojados de forma segura utilizando la infraestructura de Google Firebase, que cumple con los más altos estándares de seguridad y encriptación vigentes.</p>
        <p><strong>5. Conservación:</strong> Tu perfil y datos asociados se mantendrán activos mientras dure la relación terapéutica y hasta dos meses después de nuestra última sesión de alta. Tras este periodo, o cuando tú lo decidas, los datos serán eliminados permanentemente.</p>
        <p><strong>6. Tus Derechos (ARCO):</strong> Tienes derecho a acceder a tus datos, rectificarlos, solicitar su portabilidad o eliminación (Derecho al Olvido) en cualquier momento. Para ello, solo tienes que enviar un correo a la dirección indicada arriba.</p>
      </div>
      <button onClick={onClose} style={{ backgroundColor: '#1E3A8A', color: 'white', width: '100%', padding: '15px', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px', marginTop: '25px' }}>
        CERRAR Y VOLVER
      </button>
    </div>
  </div>
);

// ----------------- 3. PANTALLA DE ACCESO -----------------
function AuthScreen() {
  const [alias, setAlias] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState("");
  const [aceptado, setAceptado] = useState(false);
  const [showPrivacidad, setShowPrivacidad] = useState(false);

  const handleAuth = async () => {
    setError("");
    if (!aceptado) return setError("Por favor, acepta la política de privacidad.");
    try {
      if (isRegister) {
        if (!alias.trim()) return setError("Introduce tu Nombre o Alias.");
        const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
        await setDoc(doc(db, "users", userCredential.user.uid), { alias: alias, admin: false, nexoCode: Math.random().toString(36).substring(2, 7).toUpperCase() }, { merge: true });
      } else {
        await signInWithEmailAndPassword(auth, email, pass);
      }
    } catch (err) { setError("Error en el acceso. Revisa tus datos."); }
  };

  return (
    <div className="auth-container">
      <h2 style={{ fontFamily: "'Montserrat', sans-serif", color: "#1E3A8A", fontSize: '28px', fontWeight: '300', letterSpacing: '2px', marginBottom: '20px' }}>BELTRAN PSICO</h2>
      <div style={{ backgroundColor: '#F8FAFC', padding: '12px', borderRadius: '10px', border: '1px solid #CBD5E1', marginBottom: '15px', textAlign: 'justify', fontSize: '13px', color: '#475569', lineHeight: '1.4' }}>
        <strong>Aviso importante:</strong> Esta aplicación es una herramienta de apoyo complementaria a tus sesiones en consulta. Es un servicio totalmente gratuito durante tu proceso terapéutico.
      </div>
      
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '20px', fontSize: '13px', color: '#1E293B', textAlign: 'justify', cursor: 'pointer' }}>
        <input type="checkbox" checked={aceptado} onChange={e => setAceptado(e.target.checked)} style={{ width: '18px', height: '18px', flexShrink: 0, marginTop: '2px', accentColor: '#1E3A8A' }} />
        <span style={{ fontWeight: '600' }}>
          He leído y acepto la <button onClick={(e) => { e.preventDefault(); setShowPrivacidad(true); }} style={{ color: '#1E3A8A', textDecoration: 'underline', padding: 0, fontWeight: '800', display: 'inline', fontSize: '13px' }}>política de privacidad</button> conforme a la normativa de protección de datos (LOPD/RGPD).
        </span>
      </label>
      
      {error && <p style={{ color: '#B91C1C', fontSize: '13px', fontWeight: 'bold', marginBottom: '15px' }}>{error}</p>}
      {isRegister && <input type="text" placeholder="Nombre o Alias usado en consulta" value={alias} onChange={e=>setAlias(e.target.value)} style={{ marginBottom: '12px' }} />}
      <input type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} style={{ marginBottom: '12px' }} />
      <input type="password" placeholder="Contraseña" value={pass} onChange={e=>setPass(e.target.value)} style={{ marginBottom: isRegister ? '5px' : '20px' }} />
      {isRegister && <p style={{ fontSize: '11px', color: '#64748B', textAlign: 'left', marginTop: '0', marginBottom: '20px' }}>* Mínimo 8 caracteres, con mayúsculas, minúsculas y números.</p>}
      <button onClick={handleAuth} style={{ backgroundColor: aceptado ? '#1E3A8A' : '#94A3B8', color: 'white', width: '100%', padding: '12px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', marginBottom: '15px', cursor: aceptado ? 'pointer' : 'not-allowed' }}>
        {isRegister ? "CREAR CUENTA" : "ENTRAR"}
      </button>
      <button onClick={() => { setIsRegister(!isRegister); setError(""); }} style={{ color: '#64748B', fontSize: '14px' }}>
        {isRegister ? "¿Ya tienes cuenta? Entra aquí" : "¿No tienes cuenta? Regístrate"}
      </button>

      {showPrivacidad && <ModalPrivacidad onClose={() => setShowPrivacidad(false)} />}
    </div>
  );
}

// ----------------- 4. ICONOS SVG -----------------
const IconPersona = ({ color, size = "50" }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const IconFamilia = ({ color, size = "60" }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>;
const IconDiana = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>;
const IconEstrella = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2z"></path></svg>;
const IconBrujula = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon></svg>;
const IconGrafico = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>;
const IconLapiz = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const IconCerebro = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A5 5 0 0 1 12 7v10a5 5 0 0 1-5-5 5 5 0 0 1 2.5-8.66Z"></path><path d="M14.5 2A5 5 0 0 0 12 7v10a5 5 0 0 0 5-5 5 5 0 0 0-2.5-8.66Z"></path></svg>;
const IconCaja = ({ color }) => <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path><path d="M12 22V12"></path><path d="m3.3 7 8.7 5 8.7-5"></path></svg>;
const IconSol = ({ color }) => <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"></path></svg>;
const IconEscudo = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>;
const IconCampana = ({ color }) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>;
const IconVincular = ({ color }) => <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>;

// ----------------- 5. COMPONENTES COMUNES Y MODALES -----------------
const HeaderSeccion = ({ titulo, volverRuta, color }) => {
  const navigate = useNavigate();
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px', width: '100%', minHeight: '40px' }}>
      {volverRuta && <button onClick={() => navigate(volverRuta)} style={{ position: 'absolute', left: 0, width: '40px', height: '40px', borderRadius: '12px', backgroundColor: 'white', border: '2px solid #E2E8F0', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '24px', color: '#64748B', zIndex: 10 }}>←</button>}
      <h1 style={{ fontSize: '22px', fontWeight: '800', margin: 0, color: color, textTransform: 'uppercase', textAlign: 'center', padding: '0 50px', width: '100%', lineHeight: '1.2' }}>{titulo}</h1>
    </div>
  );
};

const FooterFirma = () => {
  const navigate = useNavigate();
  const [sugOpen, setSugOpen] = useState(false);
  const [sugText, setSugText] = useState("");
  const enviarSugerencia = async () => {
    if(!sugText.trim()) return;
    try {
      await addDoc(collection(db, "sugerencias"), { uid: auth.currentUser.uid, texto: sugText, fecha: new Date().toISOString() });
      alert("¡Sugerencia enviada!");
      setSugOpen(false); setSugText("");
    } catch (e) { alert("Error al enviar."); }
  };

  return (
    <>
      <div style={{ width: '100%', marginTop: 'auto' }}>
        <div style={{ backgroundColor: '#DBEAFE', padding: '15px 10px', textAlign: 'center', borderTop: '2px solid #BFDBFE', borderBottom: '2px solid #BFDBFE' }}>
          <p style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#1E3A8A', fontWeight: '600', lineHeight: '1.4' }}>
            🛠️ <strong>Fase de prueba.</strong><br />Tus comentarios ayudan a mejorar.
          </p>
          <button onClick={() => setSugOpen(true)} style={{ backgroundColor: '#1E3A8A', color: 'white', padding: '10px 20px', borderRadius: '10px', fontSize: '15px', fontWeight: '800', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>💡 ENVIAR SUGERENCIA</button>
        </div>
        <div style={{ textAlign: 'center', color: '#1E293B', padding: '20px 15px', fontSize: '14px', backgroundColor: '#ffffff' }}>
          <strong style={{ display: 'block', marginBottom: '5px' }}>Francisco Beltrán</strong>
          <span style={{ fontWeight: '400' }}>Psicólogo - Trabajando juntos en el día a día.</span><br/>
          <button onClick={() => navigate('/normas')} style={{ marginTop: '15px', color: '#64748B', fontSize: '14px', fontWeight: '700', textDecoration: 'underline' }}>📖 Normas y Protocolo</button><br/>
          <button onClick={() => { sessionStorage.clear(); localStorage.clear(); signOut(auth); }} style={{ marginTop: '15px', color: '#B91C1C', fontSize: '14px', fontWeight: '600' }}>[ Cerrar Sesión ]</button>
        </div>
      </div>
      {sugOpen && (
        <div style={{ position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.6)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 5000 }}>
          <div style={{ backgroundColor:'white', padding:'25px', borderRadius:'20px', width:'90%', maxWidth:'350px' }}>
            <h3 style={{marginTop:0, color:'#1E3A8A', textAlign:'center', fontSize: '18px'}}>Buzón de Sugerencias</h3>
            <AutoTextarea value={sugText} onChange={e=>setSugText(e.target.value)} placeholder="Ej: Me gustaría poder subir fotos..." />
            <div style={{ display:'flex', gap:'10px', marginTop:'15px' }}>
              <button onClick={()=>setSugOpen(false)} style={{flex:1, padding:'10px', borderRadius:'10px', backgroundColor:'#E2E8F0', fontWeight:'700'}}>Cancelar</button>
              <button onClick={enviarSugerencia} style={{flex:1, padding:'10px', borderRadius:'10px', backgroundColor:'#1E3A8A', color:'white', fontWeight:'700'}}>Enviar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const RecordatorioModal = ({ itemTexto, onClose, onSave, isCompartido, color }) => {
  const [frecuencia, setFrecuencia] = useState("una");
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("");
  const [cantidad, setCantidad] = useState("1");
  const [compartir, setCompartir] = useState(false);

  if (!itemTexto) return null;

  const handleSave = () => {
    if(!fecha || !hora) return alert("Selecciona el día y la hora.");
    let fechas = [];
    let baseDate = new Date(`${fecha}T${hora}`);
    let count = frecuencia === 'una' ? 1 : parseInt(cantidad);
    for(let i=0; i<count; i++) {
      let nextDate = new Date(baseDate);
      if (frecuencia === 'diario') nextDate.setDate(nextDate.getDate() + i);
      if (frecuencia === 'semanal') nextDate.setDate(nextDate.getDate() + (i * 7));
      if (frecuencia === 'quincenal') nextDate.setDate(nextDate.getDate() + (i * 14));
      fechas.push(nextDate.toISOString());
    }
    onSave(fechas, isCompartido ? compartir : true, itemTexto);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 6000 }}>
      <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '20px', width: '90%', maxWidth: '350px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0, color: color, fontSize: '20px', textAlign: 'center' }}>🔔 PROGRAMAR AVISO</h3>
        <p style={{ fontSize: '15px', color: '#475569', textAlign: 'center', fontStyle: 'italic', marginBottom: '20px', backgroundColor: '#F8FAFC', padding: '10px', borderRadius: '8px' }}>"{itemTexto}"</p>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>¿Con qué frecuencia?</label>
        <select value={frecuencia} onChange={e=>setFrecuencia(e.target.value)} style={{ marginBottom: '15px', padding: '8px', fontSize: '14px' }}>
          <option value="una">Una sola vez</option>
          <option value="diario">Todos los días</option>
          <option value="semanal">Semanal (mismo día)</option>
          <option value="quincenal">Cada 2 semanas</option>
        </select>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>¿Cuándo empezamos?</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
          <input type="date" value={fecha} onChange={e=>setFecha(e.target.value)} style={{ flex: 1, padding: '8px', fontSize: '14px' }} />
          <input type="time" value={hora} onChange={e=>setHora(e.target.value)} style={{ flex: 1, padding: '8px', fontSize: '14px' }} />
        </div>
        {frecuencia !== 'una' && (
          <>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>¿Cuántas repeticiones?</label>
            <input type="number" min="1" max="365" value={cantidad} onChange={e=>setCantidad(e.target.value)} style={{ marginBottom: '15px', padding: '8px', fontSize: '14px' }} />
          </>
        )}
        {isCompartido && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: '600', color: color, marginBottom: '20px', backgroundColor: '#F1F5F9', padding: '10px', borderRadius: '10px' }}>
            <input type="checkbox" checked={compartir} onChange={e=>setCompartir(e.target.checked)} style={{ width: '20px', height: '20px' }} /> Compartir en el tablón común
          </label>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: '10px', backgroundColor: '#E2E8F0', fontWeight: '700' }}>Cancelar</button>
          <button onClick={handleSave} style={{ flex: 1, padding: '12px', borderRadius: '10px', backgroundColor: color, color: 'white', fontWeight: '700' }}>Guardar</button>
        </div>
      </div>
    </div>
  );
};

// ----------------- COMPONENTE: FRASE DEL DÍA -----------------
const TarjetaFraseDia = ({ uid, userData }) => {
  const [frase, setFrase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [accionTxt, setAccionTxt] = useState("");
  const [showRecordatorio, setShowRecordatorio] = useState(false);
  const c = "#4338CA";

  useEffect(() => {
    const fetchFrase = async () => {
      if (!uid || !userData) return;
      const hoy = new Date();
      const hoyStr = hoy.toLocaleDateString('es-ES');
      const hidden = localStorage.getItem(`hide_frase_${uid}`);
      if (hidden === hoyStr) return;
      const diaSemana = hoy.getDay(); 
      if (diaSemana === 0) return; // Regla del Domingo

      const isLinked = userData?.partnerUid || userData?.familyGroupId;
      const dayOfYear = Math.floor((hoy - new Date(hoy.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
      let collectionName = (isLinked && (diaSemana === 3 || diaSemana === 6)) ? "frases_compartidas" : "frases_diarias";

      let snap = await getDocs(collection(db, collectionName));
      if (snap.empty && collectionName === "frases_compartidas") snap = await getDocs(collection(db, "frases_diarias"));
      
      if (!snap.empty) {
        // AQUÍ ESTÁ EL ORDEN ESTABLE (.sort)
        const frasesArray = snap.docs.map(d => d.data().texto).sort();
        setFrase(frasesArray[dayOfYear % frasesArray.length]);
      }
    }; fetchFrase();
  }, [uid, userData]);

  const ocultarHoy = () => { localStorage.setItem(`hide_frase_${uid}`, new Date().toLocaleDateString('es-ES')); setFrase(null); };

  const guardar = async (colName, textoGuardar) => {
    const ref = doc(db, colName, uid);
    const snap = await getDoc(ref);
    const data = snap.exists() ? (snap.data().items || []) : [];
    await setDoc(ref, { items: [{ id: Date.now(), t: textoGuardar, est: null, fecha: colName === 'recordatorios_ind' ? null : undefined }, ...data] }, { merge: true });
    alert("Guardado."); ocultarHoy();
  };

  const guardarRecordatorioConFecha = async (fechasArray, compartir, textoAccion) => {
    const ref = doc(db, "recordatorios_ind", uid);
    const snap = await getDoc(ref);
    const data = snap.exists() ? (snap.data().items || []) : [];
    const nuevosItems = fechasArray.map((f, i) => ({ id: Date.now() + i, t: textoAccion, fecha: f, est: 'rojo' }));
    await setDoc(ref, { items: [...nuevosItems, ...data] }, { merge: true });
    alert(`Programado en tu Tablón.`); ocultarHoy();
  };

  if (!frase) return null;

  return (
    <>
      <div style={{ backgroundColor: '#EEF2FF', border: `2px solid ${c}`, borderRadius: '20px', padding: '20px', marginBottom: '20px', boxShadow: '0 5px 15px rgba(67,56,202,0.1)' }}>
        <h3 style={{ margin: '0 0 10px 0', color: c, fontSize: '16px', textTransform: 'uppercase' }}>✨ Reflexión del Día</h3>
        <p style={{ fontSize: '18px', fontStyle: 'italic', color: '#1E293B', lineHeight: '1.4', marginBottom: '15px', fontWeight: '600' }}>"{frase}"</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={() => { setAccionTxt(frase); setShowModal('directo'); }} style={{ backgroundColor: c, color: 'white', padding: '10px', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px' }}>💡 Me la guardo tal cual</button>
          <button onClick={() => { setAccionTxt(""); setShowModal('accion'); }} style={{ backgroundColor: 'white', color: c, border: `2px solid ${c}`, padding: '10px', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px' }}>🛠️ La transformo en una acción</button>
          <button onClick={ocultarHoy} style={{ color: '#64748B', fontWeight: 'bold', fontSize: '14px', padding: '8px', textDecoration: 'underline' }}>😌 Solo reflexiono por hoy</button>
        </div>
      </div>
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '20px', width: '90%', maxWidth: '350px' }}>
            <h3 style={{ marginTop: 0, color: c, textAlign: 'center', fontSize: '18px' }}>¿Qué quieres hacer con esto?</h3>
            {showModal === 'accion' && <AutoTextarea value={accionTxt} onChange={e=>setAccionTxt(e.target.value)} placeholder="Ej: Hoy voy a sonreír al llegar..." style={{ marginBottom: '15px' }} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={() => { if(accionTxt.trim()) guardar('recordatorios_ind', accionTxt); setShowModal(false); }} style={{ backgroundColor: '#F1F5F9', padding: '12px', borderRadius: '10px', fontWeight: 'bold', border: '1px solid #CBD5E1' }}>📌 Al Tablón (Sin fecha)</button>
              <button onClick={() => { if(accionTxt.trim()) setShowRecordatorio(true); setShowModal(false); }} style={{ backgroundColor: c, color: 'white', padding: '12px', borderRadius: '10px', fontWeight: 'bold' }}>⏰ Programar un Aviso</button>
              <button onClick={() => setShowModal(false)} style={{ color: '#B91C1C', fontWeight: 'bold', padding: '10px', marginTop: '5px' }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {showRecordatorio && <RecordatorioModal itemTexto={accionTxt} onClose={() => setShowRecordatorio(false)} onSave={guardarRecordatorioConFecha} isCompartido={false} color={c} />}
    </>
  );
};

// ----------------- 7. MOTORES COMPLETOS DE HERRAMIENTAS -----------------
function EvaluacionInicial({ uid, isVoluntario }) {
  const navigate = useNavigate();
  const [emociones, setEmociones] = useState({ Miedo: 5, Tristeza: 5, Tranquilidad: 5, Autoestima: 5, Inseguridad: 5, Alegría: 5, Ansiedad: 5, Irritabilidad: 5, Energía: 5, Culpa: 5 });
  const [rueda, setRueda] = useState({ 'Salud Física': 5, Alimentación: 5, Ocio: 5, 'Vida Social': 5, Pareja: 5, Trabajo: 5, Familia: 5 });
  const [nuevaEmo, setNuevaEmo] = useState("");
  const [nuevaArea, setNuevaArea] = useState("");
  const [mostrarEvolucion, setMostrarEvolucion] = useState(false);
  const [historialEvals, setHistorialEvals] = useState([]);
  useEffect(() => {
    const loadEval = async () => {
      if(!uid) return;
      const snap = await getDoc(doc(db, "evaluaciones", uid));
      if (snap.exists()) {
        if(snap.data().emociones) setEmociones(snap.data().emociones);
        if(snap.data().rueda) setRueda(snap.data().rueda);
      }
    }; loadEval();
  }, [uid]);

  const agregarEmocion = () => { if(nuevaEmo.trim() !== "") { setEmociones({...emociones, [nuevaEmo.trim()]: 5}); setNuevaEmo(""); } };
  const agregarArea = () => { if(nuevaArea.trim() !== "") { setRueda({...rueda, [nuevaArea.trim()]: 5}); setNuevaArea(""); } };

const guardar = async () => {
    try {
      const ref = doc(db, "evaluaciones", uid);
      const snap = await getDoc(ref);
      let historialAntiguo = [];
      if (snap.exists() && snap.data().historial) {
        historialAntiguo = snap.data().historial;
      }

      const evaluacionHoy = { emociones, rueda, fecha: Date.now() };
      const nuevoHistorial = [evaluacionHoy, ...historialAntiguo];

      await setDoc(ref, { 
        emociones, 
        rueda, 
        fecha: Date.now(),
        historial: nuevoHistorial 
      }, { merge: true });
      
      await setDoc(doc(db, "users", uid), { lastEval: Date.now() }, { merge: true });
      
      setHistorialEvals(nuevoHistorial);
      alert("¡Evaluación guardada correctamente!");
      setMostrarEvolucion(true);
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error("Fallo técnico al guardar:", error);
      alert("ERROR: No se ha podido guardar la evaluación.");
    }
  };
  
// --- PANTALLA FASE 2: LA EVOLUCIÓN ---
  if (mostrarEvolucion) {
    // Extraemos las evaluaciones anteriores de la memoria
    const evalAnterior = historialEvals[1]; // La penúltima
    const evalMasAntigua = historialEvals[2]; // La antepenúltima

    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'sans-serif' }}>
        <h2 style={{ color: '#1E3A8A', textAlign: 'center', marginBottom: '5px' }}>Tu Evolución</h2>
        <p style={{ textAlign: 'center', color: '#6B7280', marginBottom: '20px', fontSize: '0.95rem' }}>
          Desliza la tabla hacia los lados para ver tus registros anteriores.
        </p>
        
        <div style={{ overflowX: 'auto', marginBottom: '30px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', backgroundColor: 'white' }}>
            <thead style={{ backgroundColor: '#1E3A8A', color: 'white' }}>
              <tr>
                <th style={{ padding: '12px', whiteSpace: 'nowrap', textAlign: 'left' }}>Área Evaluada</th>
                <th style={{ padding: '12px', whiteSpace: 'nowrap', backgroundColor: '#1D4ED8' }}>HOY</th>
                <th style={{ padding: '12px', whiteSpace: 'nowrap' }}>Evaluación Anterior</th>
                <th style={{ padding: '12px', whiteSpace: 'nowrap' }}>Más antigua</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ backgroundColor: '#F3F4F6' }}><td colSpan="4" style={{ padding: '8px', fontWeight: 'bold', textAlign: 'left' }}>🧠 Emociones</td></tr>
              {Object.keys(emociones).map((emo, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '10px', textAlign: 'left', fontWeight: 'bold', color: '#374151' }}>{emo}</td>
                  <td style={{ padding: '10px', fontWeight: 'bold', color: '#2563EB', backgroundColor: '#EFF6FF' }}>{emociones[emo]}</td>
                  <td style={{ padding: '10px', color: '#6B7280' }}>{evalAnterior && evalAnterior.emociones ? evalAnterior.emociones[emo] : '-'}</td>
                  <td style={{ padding: '10px', color: '#6B7280' }}>{evalMasAntigua && evalMasAntigua.emociones ? evalMasAntigua.emociones[emo] : '-'}</td>
                </tr>
              ))}
              
              <tr style={{ backgroundColor: '#F3F4F6' }}><td colSpan="4" style={{ padding: '8px', fontWeight: 'bold', textAlign: 'left' }}>🎡 Rueda de la Vida</td></tr>
              {Object.keys(rueda).map((area, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '10px', textAlign: 'left', fontWeight: 'bold', color: '#374151' }}>{area}</td>
                  <td style={{ padding: '10px', fontWeight: 'bold', color: '#2563EB', backgroundColor: '#EFF6FF' }}>{rueda[area]}</td>
                  <td style={{ padding: '10px', color: '#6B7280' }}>{evalAnterior && evalAnterior.rueda ? evalAnterior.rueda[area] : '-'}</td>
                  <td style={{ padding: '10px', color: '#6B7280' }}>{evalMasAntigua && evalMasAntigua.rueda ? evalMasAntigua.rueda[area] : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

{/* Llamada a la acción y Salida segura */}
        <div style={{ backgroundColor: '#F0FDF4', padding: '20px', borderRadius: '8px', textAlign: 'center', border: '1px solid #BBF7D0' }}>
          <h3 style={{ color: '#166534', marginTop: '0', marginBottom: '10px' }}>🎯 Tu Objetivo para esta Quincena</h3>
          <p style={{ color: '#15803D', fontSize: '0.95rem', marginBottom: '20px' }}>
            Teniendo en cuenta tus resultados de hoy, ¿qué pequeña acción realista te vas a proponer mejorar?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            <button 
              onClick={() => navigate('/objetivos-individuales')} 
              style={{ backgroundColor: '#16A34A', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', width: '100%', maxWidth: '300px' }}>
              Crear Objetivo Ahora
            </button>
            <button 
              onClick={() => navigate('/')} 
              style={{ backgroundColor: '#E2E8F0', color: '#475569', padding: '12px 24px', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', width: '100%', maxWidth: '300px' }}>
              🏠 Continuar a mi Inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
  // --- FIN DE LA PANTALLA DE EVOLUCIÓN ---

    const enviarAObjetivos = async (area) => {
    if(!uid) return;
    const ref = doc(db, "obj_ind", uid);
    const snap = await getDoc(ref);
    const objsData = snap.exists() ? (snap.data().objs || []) : [];
    await setDoc(ref, { objs: [{ id: Date.now(), t: `Mejorar mi satisfacción en: ${area}`, ac: [] }, ...objsData] }, { merge: true });
    alert(`¡'${area}' se ha enviado a Mis Objetivos!`);
  };

  const renderSliders = (datos, setDatos, color, esRueda) => {
    return Object.keys(datos).map(k => (
      <div key={k} style={{ marginBottom: '10px', backgroundColor: '#F8FAFC', padding: '12px', borderRadius: '12px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ width: '90px', fontWeight: 'bold', color: '#334155', fontSize: '13px', lineHeight: '1.2' }}>{k}</span>
          <input type="range" min="0" max="10" value={datos[k]} onChange={(e) => setDatos({...datos, [k]: parseInt(e.target.value)})} style={{ flex: 1, margin: '0 8px', accentColor: color }} />
          <span style={{ fontWeight: '900', color: color, width: '20px', textAlign: 'right', fontSize: '15px' }}>{datos[k]}</span>
        </div>
        {esRueda && datos[k] < 5 && (
          <button onClick={() => enviarAObjetivos(k)} style={{ alignSelf: 'flex-start', marginTop: '8px', backgroundColor: '#DBEAFE', color: '#1E3A8A', padding: '6px 10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', border: '1px solid #BFDBFE' }}>
            🎯 Convertir en Objetivo
          </button>
        )}
      </div>
    ));
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: '#F8FAFC', paddingBottom: '30px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {isVoluntario && <HeaderSeccion titulo="MI EVALUACIÓN" volverRuta={-1} color="#1E3A8A" />}
        {!isVoluntario && <h2 style={{ color: '#1E3A8A', textAlign: 'center', margin: '0 0 15px 0', fontSize: '22px', fontWeight: '900' }}>REVISIÓN QUINCENAL</h2>}
        
        <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '12px', borderLeft: '5px solid #1E3A8A', marginBottom: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
          <p style={{ fontSize: '14px', color: '#475569', margin: 0, lineHeight: '1.5', textAlign: 'justify' }}>
            Ajusta los valores según cómo te hayas sentido estos últimos días. Si alguna de las áreas de tu Rueda de la Vida tiene una puntuación baja, podrás enviarla a tu lista de objetivos para trabajarla.
          </p>
        </div>

        <h3 style={{ color: '#047857', marginTop: '20px', marginBottom: '10px', fontSize: '16px' }}>1. Mi estado emocional reciente</h3>
        {renderSliders(emociones, setEmociones, '#047857', false)}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <input type="text" value={nuevaEmo} onChange={e=>setNuevaEmo(e.target.value)} placeholder="Añadir otra emoción..." />
          <button onClick={agregarEmocion} style={{ backgroundColor: '#D1FAE5', color: '#047857', padding: '0 12px', borderRadius: '10px', fontWeight: 'bold', border: '1px solid #34D399' }}>➕</button>
        </div>

        <h3 style={{ color: '#B91C1C', marginTop: '30px', marginBottom: '10px', fontSize: '16px' }}>2. Mi Rueda de la Vida</h3>
        {renderSliders(rueda, setRueda, '#B91C1C', true)}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <input type="text" value={nuevaArea} onChange={e=>setNuevaArea(e.target.value)} placeholder="Añadir otra área..." />
          <button onClick={agregarArea} style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '0 12px', borderRadius: '10px', fontWeight: 'bold', border: '1px solid #F87171' }}>➕</button>
        </div>

        <button onClick={guardar} style={{ width: '100%', backgroundColor: '#1E3A8A', color: 'white', padding: '15px', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px', marginTop: '30px' }}>
          GUARDAR EVALUACIÓN
        </button>
      </div>
    </div>
  );
}

function MotorAutoestima({ titulo, color, bg, docUid }) {
  const [tab, setTab] = useState("soy");
  const [data, setData] = useState({ virtudes: [], char_mejorar: [], cond_pos: [], cond_mejorar: [], pens_pos: [], pens_neg: [] });
  const [inputs, setInputs] = useState({ virtudes: "", char_mejorar: "", cond_pos: "", cond_mejorar: "", pens_pos: "", pens_neg: "" });
  const targetUid = docUid || auth.currentUser?.uid;
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      if(!targetUid) return;
      const snap = await getDoc(doc(db, "autoestima_ind", targetUid));
      if (snap.exists()) setData(snap.data());
    }; load();
  }, [targetUid]);

  const guardarData = async (newData) => { setData(newData); if(targetUid) await setDoc(doc(db, "autoestima_ind", targetUid), newData, { merge: true }); };
  const addItem = (lista) => { if(inputs[lista].trim()){ guardarData({ ...data, [lista]: [{id: Date.now(), t: inputs[lista]}, ...(data[lista]||[])] }); setInputs({...inputs, [lista]: ""}); } };
  const removeItem = (lista, id) => { if(window.confirm("¿Borrar?")) guardarData({ ...data, [lista]: (data[lista]||[]).filter(x => x.id !== id) }); };

  const enviarAObjetivos = async (texto) => {
    if(!targetUid) return;
    const ref = doc(db, "obj_ind", targetUid);
    const snap = await getDoc(ref);
    const objsData = snap.exists() ? (snap.data().objs || []) : [];
    await setDoc(ref, { objs: [{ id: Date.now(), t: `Trabajar en: ${texto}`, ac: [] }, ...objsData] }, { merge: true });
    alert("¡Enviado a Mis Objetivos!");
  };

  const renderList = (listaKey, tituloLista, placeholder, esNegativo, accionEspecial) => (
    <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '16px', border: `2px solid ${esNegativo ? '#CBD5E1' : color}`, marginBottom: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
      <h3 style={{ color: esNegativo ? '#475569' : color, marginTop: 0, fontSize: '15px', marginBottom: '10px' }}>{tituloLista}</h3>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}><AutoTextarea value={inputs[listaKey]} onChange={e=>setInputs({...inputs, [listaKey]: e.target.value})} placeholder={placeholder} /></div>
        <button onClick={()=>addItem(listaKey)} style={{ backgroundColor: esNegativo ? '#475569' : color, color: 'white', padding: '6px 10px', borderRadius: '10px', fontWeight: 'bold', height: 'fit-content', fontSize: '13px' }}>AÑADIR</button>
      </div>
      {(data[listaKey] || []).map(it => (
        <div key={it.id} style={{ backgroundColor: '#F8FAFC', padding: '8px 10px', borderRadius: '10px', marginBottom: '6px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '14px', flex: 1, paddingRight: '10px', color: '#1E293B', lineHeight: '1.3' }}>{it.t}</span>
            <button onClick={()=>removeItem(listaKey, it.id)} style={{ fontSize: '16px' }}>🗑️</button>
          </div>
          {accionEspecial === 'objetivo' && <button onClick={()=>enviarAObjetivos(it.t)} style={{ backgroundColor: '#DBEAFE', color: '#1E3A8A', padding: '8px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', border: '1px solid #BFDBFE', marginTop: '2px', alignSelf: 'flex-start' }}>🎯 Convertir en Objetivo</button>}
          {accionEspecial === 'pec' && <button onClick={()=>navigate('/procesos-pec-individual')} style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '8px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', border: '1px solid #FECACA', marginTop: '2px', alignSelf: 'flex-start' }}>🧠 Analizar en P-E-C</button>}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo={titulo} volverRuta={-1} color={color} />
        <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '12px', borderLeft: `5px solid ${color}`, marginBottom: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
          <p style={{ fontSize: '13px', color: '#334155', margin: 0, lineHeight: '1.5', textAlign: 'justify' }}>
            <strong>La autoestima</strong> es la valoración profunda que hacemos de nosotros mismos y parte de la sinceridad con uno mismo. Por eso es necesario conocer lo que soy: defectos y virtudes; lo que me digo: mis pensamientos positivos y negativos; y lo que hago: mis conductas positivas y negativas. No consiste en creer que somos perfectos, sino en conocernos y aceptarnos de forma honesta, pero siendo consciente de que hay aspectos que hay que reducir (excesivo desorden) o eliminar (violencia física). Sólo siendo consciente de lo que hay puedo empezar a mejorar. La autoestima sube cuando hago las cosas bien y baja cuando yo mismo soy consciente de que mis comportamientos no son los adecuados. La clave es que yo tenga esa actitud de reducir lo negativo o eliminarlo si es muy grave, sin pretender ser perfecto. Es entonces cuando mi mente está contenta con ella misma y se siente segura porque sabe que está gestionando bien las situaciones.
          </p>
        </div>
        <div style={{ backgroundColor: 'white', padding: '10px', borderRadius: '12px', border: `1px dashed ${color}44`, marginBottom: '15px', fontSize: '13px', color: '#475569', textAlign: 'center' }}>
          ℹ️ {tab === 'soy' ? "Tu identidad: las cualidades que te definen y los rasgos a trabajar." : tab === 'hago' ? "Tus acciones: hábitos positivos y conductas a modificar." : "Tu diálogo interno: las palabras amables y los pensamientos que te frenan."}
        </div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
          {[['soy', 'LO QUE SOY'], ['hago', 'LO QUE HAGO'], ['digo', 'LO QUE ME DIGO']].map(([id, txt]) => (
            <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: '10px 4px', borderRadius: '10px', fontSize: '12px', fontWeight: '800', backgroundColor: tab === id ? color : '#F1F5F9', color: tab === id ? 'white' : '#64748B', border: tab === id ? `2px solid ${color}` : '2px solid transparent', transition: 'all 0.2s' }}>{txt}</button>
          ))}
        </div>
        {tab === 'soy' && (<>{renderList('virtudes', 'Mis Virtudes', 'Ej: Soy leal...', false, null)}{renderList('char_mejorar', 'Mis características a mejorar', 'Ej: Soy impaciente...', true, 'objetivo')}</>)}
        {tab === 'hago' && (<>{renderList('cond_pos', 'Mis conductas positivas', 'Ej: Cuido mi alimentación...', false, null)}{renderList('cond_mejorar', 'Mis conductas a mejorar', 'Ej: Me aíslo al enfadarme...', true, 'objetivo')}</>)}
        {tab === 'digo' && (<>{renderList('pens_pos', 'Pensamientos positivos', 'Ej: Hoy lo he hecho bien...', false, null)}{renderList('pens_neg', 'Pensamientos negativos', 'Ej: Siempre lo estropeo todo...', true, 'pec')}</>)}
      </div>
      <FooterFirma />
    </div>
  );
}

function TablonRecordatorios({ color, docUid, collectionName, titulo }) {
  const [items, setItems] = useState([]);
  const [showExito, setShowExito] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      const uidToUse = docUid || auth.currentUser?.uid;
      if(!uidToUse) return;
      const snap = await getDoc(doc(db, collectionName, uidToUse));
      if (snap.exists()) setItems(snap.data().items || []);
    }; cargar();
  }, [docUid, collectionName]);

  const dispararExito = () => { setShowExito(true); setTimeout(() => setShowExito(false), 3000); };
  const guardar = async (nuevos) => { const uidToUse = docUid || auth.currentUser?.uid; setItems(nuevos); if(uidToUse) await setDoc(doc(db, collectionName, uidToUse), {items: nuevos}); };
  const remove = (id) => { if(window.confirm("¿Eliminar?")) guardar(items.filter(x=>x.id!==id)); };
  const setEstado = (id, estado) => { guardar(items.map(it=>it.id===id?{...it, est:estado}:it)); if (estado === 'verde') dispararExito(); };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: color === '#047857' ? '#ECFDF5' : (color==='#B91C1C'?'#FEF2F2':'#F8FAFC'), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ToastExito mostrar={showExito} />
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo={titulo} volverRuta={-1} color={color} />
        {items.length === 0 && <div style={{ textAlign: 'center', fontSize: '16px', color: '#64748B' }}>No hay tareas programadas.</div>}
        {items.map((it) => (
          <div key={it.id} style={{ backgroundColor: 'white', padding: '10px', borderRadius: '12px', marginBottom: '10px', border: '1px solid #E2E8F0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1E293B', lineHeight: '1.3', paddingRight: '10px' }}>{it.t}</div>
              <button onClick={() => remove(it.id)} style={{ fontSize: '16px', color: '#94A3B8' }}>🗑️</button>
            </div>
            {it.fecha && <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '10px' }}>📅 {it.fecha}</div>}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setEstado(it.id, 'rojo')} style={{ flex: 1, padding: '6px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', backgroundColor: it.est === 'rojo' ? '#FEE2E2' : '#F1F5F9', color: it.est === 'rojo' ? '#B91C1C' : '#64748B', border: it.est === 'rojo' ? '1px solid #EF4444' : '1px solid transparent' }}>❌ No hecho</button>
              <button onClick={() => setEstado(it.id, 'verde')} style={{ flex: 1, padding: '6px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', backgroundColor: it.est === 'verde' ? '#D1FAE5' : '#F1F5F9', color: it.est === 'verde' ? '#047857' : '#64748B', border: it.est === 'verde' ? '1px solid #10B981' : '1px solid transparent' }}>✅ Hecho</button>
            </div>
          </div>
        ))}
      </div>
      <FooterFirma />
    </div>
  );
}

function HerramientaBase({ titulo, color, bg, storageKey, placeholder, explicacion, docUid, numerado, accionEspecial, collectionObjetivos }) {
  const [items, setItems] = useState([]);
  const [texto, setTexto] = useState("");
  const [editId, setEditId] = useState(null);
  const [editTxt, setEditTxt] = useState("");
  const targetUid = docUid || auth.currentUser?.uid;

  useEffect(() => {
    const load = async () => {
      if(!targetUid) return;
      const snap = await getDoc(doc(db, storageKey, targetUid));
      if (snap.exists()) setItems(snap.data().items || []);
    }; load();
  }, [storageKey, targetUid]);

  const guardar = async (nuevos) => { setItems(nuevos); if(targetUid) await setDoc(doc(db, storageKey, targetUid), {items: nuevos}); };
  const add = () => { if(texto.trim()){ guardar([{id: Date.now(), t: texto}, ...items]); setTexto(""); } };
  const remove = (id) => { if(window.confirm("¿Borrar?")) guardar(items.filter(x => x.id !== id)); };
  const saveEdit = (id) => { if(editTxt.trim()) { guardar(items.map(x => x.id === id ? {...x, t: editTxt} : x)); setEditId(null); } };

  const enviarAObjetivos = async (textoValor) => {
    if(!targetUid || !collectionObjetivos) return;
    const ref = doc(db, collectionObjetivos, targetUid);
    const snap = await getDoc(ref);
    const objsData = snap.exists() ? (snap.data().objs || []) : [];
    await setDoc(ref, { objs: [{ id: Date.now(), t: `Vivir mi valor: ${textoValor}`, ac: [] }, ...objsData] }, { merge: true });
    alert("¡Enviado a Mis Objetivos!");
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo={titulo} volverRuta={-1} color={color} />
        {explicacion && <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: `1px dashed ${color}44`, marginBottom: '15px', fontSize: '13px', color: '#475569', textAlign: 'justify', lineHeight: '1.4' }} dangerouslySetInnerHTML={{__html: explicacion}}></div>}
        
        <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '16px', border: `2px solid ${color}`, marginBottom: '10px' }}>
          <AutoTextarea value={texto} onChange={e=>setTexto(e.target.value)} placeholder={placeholder} />
          <button onClick={add} style={{backgroundColor:color, color:'white', padding:'6px 12px', borderRadius:'10px', fontWeight:'700', marginTop:'8px', float:'right', fontSize: '13px'}}>AÑADIR</button>
          <div style={{clear:'both'}}></div>
        </div>
        
        {items.map((it, index) => (
          <div key={it.id} style={{ backgroundColor: 'white', padding: '8px 10px', borderRadius: '10px', marginBottom: '6px', border: `1px solid ${color}33`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {editId === it.id ? (
                <div style={{ display:'flex', flex:1, gap:'10px' }}>
                  <AutoTextarea value={editTxt} onChange={e=>setEditTxt(e.target.value)} style={{flex:1}} />
                  <button onClick={()=>saveEdit(it.id)} style={{fontSize:'18px'}}>💾</button>
                </div>
              ) : (
                <>
                  <span style={{ fontSize: '14px', flex: 1, paddingRight: '10px', color: '#1E293B', lineHeight: '1.3' }}>
                    {numerado && <strong style={{ color: color }}>{items.length - index}. </strong>}{it.t}
                  </span>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={()=>{setEditId(it.id); setEditTxt(it.t);}} style={{fontSize:'16px'}}>✏️</button>
                    <button onClick={()=>remove(it.id)} style={{fontSize:'16px'}}>🗑️</button>
                  </div>
                </>
              )}
            </div>
            {accionEspecial === 'objetivo' && !editId && (
              <button onClick={()=>enviarAObjetivos(it.t)} style={{ backgroundColor: '#DBEAFE', color: '#1E3A8A', padding: '6px 10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', border: '1px solid #BFDBFE', marginTop: '2px', alignSelf: 'flex-start' }}>🎯 Convertir en Objetivo</button>
            )}
          </div>
        ))}
      </div>
      <FooterFirma />
    </div>
  );
}

function MotorAprendizajes({ titulo, color, bg, storageKey, placeholder, explicacion, textosSemaforo, docUid, collectionAlerts, showUsageTracker }) {
  const [items, setItems] = useState([]);
  const [texto, setTexto] = useState("");
  const [usoGlobal, setUsoGlobal] = useState("No utilizado"); 
  const [modalItemTexto, setModalItemTexto] = useState(null);
  const [showExito, setShowExito] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTxt, setEditTxt] = useState("");
  const targetUid = docUid || auth.currentUser?.uid;

  useEffect(() => {
    const load = async () => {
      if(!targetUid) return;
      const snap = await getDoc(doc(db, storageKey, targetUid));
      if (snap.exists()) { setItems(snap.data().items || []); if(snap.data().uso) setUsoGlobal(snap.data().uso); }
    }; load();
  }, [storageKey, targetUid]);

  const dispararExito = () => { setShowExito(true); setTimeout(() => setShowExito(false), 3000); };
  const guardar = async (nuevosItems, nuevoUso = usoGlobal) => { setItems(nuevosItems); setUsoGlobal(nuevoUso); if(targetUid) await setDoc(doc(db, storageKey, targetUid), {items: nuevosItems, uso: nuevoUso}, {merge:true}); };
  const add = () => { if(texto.trim()){ guardar([{id: Date.now(), t: texto, est: null}, ...items]); setTexto(""); } };
  const remove = (id) => { if(window.confirm("¿Borrar?")) guardar(items.filter(x => x.id !== id)); };
  const saveEdit = (id) => { if(editTxt.trim()) { guardar(items.map(x => x.id === id ? {...x, t: editTxt} : x)); setEditId(null); } };
  const setEstado = (id, estado, itemText) => { guardar(items.map(it => it.id === id ? { ...it, est: estado } : it)); if (estado === 'verde') dispararExito(); if (estado === 'rojo' || estado === 'amarillo') setModalItemTexto(itemText); };

  const guardarRecordatorio = async (fechasArray, compartir, textoAccion) => {
    const uidToUse = docUid || auth.currentUser?.uid;
    const col = collectionAlerts || "recordatorios_ind"; 
    if (uidToUse) {
      const ref = doc(db, col, uidToUse);
      const snap = await getDoc(ref);
      const data = snap.exists() ? (snap.data().items || []) : [];
      const nuevosItems = fechasArray.map((f, i) => ({ id: Date.now() + i, t: textoAccion, fecha: f, est: null }));
      await setDoc(ref, { items: [...nuevosItems, ...data] });
      alert(`¡Programado en el tablón!`);
    }
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ToastExito mostrar={showExito} />
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo={titulo} volverRuta={-1} color={color} />
        {explicacion && <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: `1px dashed ${color}44`, marginBottom: '15px', fontSize: '13px', color: '#475569', textAlign: 'justify' }} dangerouslySetInnerHTML={{__html: explicacion}}></div>}
        
        {showUsageTracker && (
          <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '12px', border: `2px solid ${color}66`, marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', color: color, fontSize: '14px' }}>Frecuencia:</span>
            <select value={usoGlobal} onChange={(e)=>guardar(items, e.target.value)} style={{ width: '50%', padding: '6px', fontSize: '14px' }}>
              <option value="No utilizado">No utilizado</option>
              <option value="Utilizado">Utilizado</option>
              <option value="Muy utilizado">Muy utilizado</option>
            </select>
          </div>
        )}

        <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '16px', border: `2px solid ${color}`, marginBottom: '20px' }}>
          <AutoTextarea value={texto} onChange={e=>setTexto(e.target.value)} placeholder={placeholder} />
          <button onClick={add} style={{backgroundColor:color, color:'white', padding:'8px 15px', borderRadius:'10px', fontWeight:'700', marginTop:'8px', float:'right', fontSize: '13px'}}>AÑADIR</button>
          <div style={{clear:'both'}}></div>
        </div>
        
        {items.map((it) => (
          <div key={it.id} style={{ backgroundColor: 'white', padding: '12px', borderRadius: '16px', border: `2px solid ${color}33`, marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'flex-start' }}>
              {editId === it.id ? (
                <div style={{ display:'flex', flex:1, gap:'10px' }}>
                  <AutoTextarea value={editTxt} onChange={e=>setEditTxt(e.target.value)} style={{flex:1}} />
                  <button onClick={()=>saveEdit(it.id)} style={{fontSize:'18px'}}>💾</button>
                </div>
              ) : (
                <>
                  <span style={{ fontSize: '15px', flex:1, marginRight:'10px', color: '#1E293B' }}>{it.t}</span>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={()=>setModalItemTexto(it.t)} style={{fontSize:'16px'}} title="Al Tablón">🔔</button>
                    <button onClick={()=>{setEditId(it.id); setEditTxt(it.t);}} style={{fontSize:'16px'}}>✏️</button>
                    <button onClick={()=>remove(it.id)} style={{fontSize:'16px'}}>🗑️</button>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
              <button onClick={()=>setEstado(it.id, 'verde', it.t)} style={{ flex: 1, padding: '8px 4px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', backgroundColor: it.est==='verde'?'#10B981':'#f1f5f9', color: it.est==='verde'?'white':'#64748B' }}>{textosSemaforo[0]}</button>
              <button onClick={()=>setEstado(it.id, 'amarillo', it.t)} style={{ flex: 1, padding: '8px 4px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', backgroundColor: it.est==='amarillo'?'#F59E0B':'#f1f5f9', color: it.est==='amarillo'?'white':'#64748B' }}>{textosSemaforo[1]}</button>
              <button onClick={()=>setEstado(it.id, 'rojo', it.t)} style={{ flex: 1, padding: '8px 4px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', backgroundColor: it.est==='rojo'?'#EF4444':'#f1f5f9', color: it.est==='rojo'?'white':'#64748B' }}>{textosSemaforo[2]}</button>
            </div>
          </div>
        ))}
      </div>
      <RecordatorioModal itemTexto={modalItemTexto} onClose={() => setModalItemTexto(null)} onSave={guardarRecordatorio} isCompartido={!!docUid} color={color} />
      <FooterFirma />
    </div>
  );
}

function MotorObjetivos({ titulo, color, colorFondo, storageKey, placeholderObj, placeholderConducta, explicacion, docUid, collectionAlerts }) {
  const [objs, setObjs] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [modalItemTexto, setModalItemTexto] = useState(null);
  const [showExito, setShowExito] = useState(false);
  const [editObjId, setEditObjId] = useState(null);
  const [editObjTxt, setEditObjTxt] = useState("");
  const [editConId, setEditConId] = useState(null);
  const [editConTxt, setEditConTxt] = useState("");
  const targetUid = docUid || auth.currentUser?.uid;

  useEffect(() => {
    const load = async () => {
      if(!targetUid) return;
      const snap = await getDoc(doc(db, storageKey, targetUid));
      if (snap.exists()) setObjs(snap.data().objs || []);
    }; load();
  }, [storageKey, targetUid]);

  const dispararExito = () => { setShowExito(true); setTimeout(() => setShowExito(false), 3000); };
  const guardar = async (nuevos) => { setObjs(nuevos); if(targetUid) await setDoc(doc(db, storageKey, targetUid), {objs: nuevos}); };
  const addObj = () => { if (nuevo.trim()) { guardar([{ id: Date.now(), t: nuevo, ac: [] }, ...objs]); setNuevo(""); } };

  const guardarRecordatorio = async (fechasArray, compartir, textoAccion) => {
    const uidToUse = docUid || auth.currentUser?.uid;
    const col = collectionAlerts || "recordatorios_ind"; 
    if (uidToUse) {
      const ref = doc(db, col, uidToUse);
      const snap = await getDoc(ref);
      const data = snap.exists() ? (snap.data().items || []) : [];
      const nuevosItems = fechasArray.map((f, i) => ({ id: Date.now() + i, t: textoAccion, fecha: f, est: null }));
      await setDoc(ref, { items: [...nuevosItems, ...data] });
      alert(`¡Programado en el tablón!`);
    }
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: colorFondo, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ToastExito mostrar={showExito} />
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo={titulo} volverRuta={-1} color={color} />
        {explicacion && <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: `1px dashed ${color}44`, marginBottom: '15px', fontSize: '13px', color: '#475569', textAlign: 'justify' }} dangerouslySetInnerHTML={{__html: explicacion}}></div>}
        
        <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '16px', border: `2px solid ${color}`, display: 'flex', gap: '10px', marginBottom: '25px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}><AutoTextarea value={nuevo} onChange={e=>setNuevo(e.target.value)} placeholder={placeholderObj} /></div>
          <button onClick={addObj} style={{backgroundColor:color, color:'white', padding:'10px', borderRadius:'10px', fontWeight:'700', fontSize:'13px'}}>CREAR</button>
        </div>

        {objs.map((o) => (
          <div key={o.id} style={{ backgroundColor: 'white', padding: '12px', borderRadius: '16px', border: `2px solid ${color}22`, marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', marginBottom: '12px' }}>
              {editObjId === o.id ? (
                <div style={{ display:'flex', flex:1, gap:'8px' }}>
                  <AutoTextarea value={editObjTxt} onChange={e=>setEditObjTxt(e.target.value)} style={{flex:1, margin:0}} />
                  <button onClick={()=>{guardar(objs.map(x=>x.id===o.id?{...x, t:editObjTxt}:x)); setEditObjId(null);}} style={{fontSize:'18px'}}>💾</button>
                </div>
              ) : (
                <>
                  <h3 style={{ margin: 0, fontSize: '16px', color: color, flex:1 }}>{o.t}</h3>
                  <div style={{ display:'flex', gap:'5px' }}>
                    <button onClick={()=>{setEditObjId(o.id); setEditObjTxt(o.t);}} style={{fontSize:'16px'}}>✏️</button>
                    <button onClick={()=>{if(window.confirm("¿Borrar?")) guardar(objs.filter(x=>x.id!==o.id));}} style={{fontSize:'16px'}}>🗑️</button>
                  </div>
                </>
              )}
            </div>
            
            <ConductaInput customPlaceholder={placeholderConducta} onAdd={(txt) => guardar(objs.map(x=>x.id===o.id?{...x, ac:[{id:Date.now(), t:txt, e:'rojo'}, ...(x.ac||[])]}:x))} />

            <div style={{ marginTop: '10px' }}>
              {(o.ac || []).map(a => (
                <div key={a.id} style={{ padding: '8px', backgroundColor: '#F8FAFC', borderRadius: '10px', marginBottom: '6px', display:'flex', flexDirection:'column', gap:'8px', border: '1px solid #E2E8F0' }}>
                  {editConId === a.id ? (
                    <div style={{ display:'flex', gap:'8px' }}>
                      <AutoTextarea value={editConTxt} onChange={e=>setEditConTxt(e.target.value)} style={{flex:1, margin:0}} />
                      <button onClick={()=>{guardar(objs.map(x=>x.id===o.id?{...x, ac:x.ac.map(y=>y.id===a.id?{...y, t:editConTxt}:y)}:x)); setEditConId(null);}} style={{fontSize:'18px'}}>💾</button>
                    </div>
                  ) : (
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <span style={{fontSize:'14px', flex:1, paddingRight:'8px'}}>{a.t}</span>
                      <div style={{ display:'flex', gap:'5px' }}>
                        <button onClick={()=>setModalItemTexto(a.t)} style={{fontSize:'16px'}} title="Al Tablón">🔔</button>
                        <button onClick={()=>{setEditConId(a.id); setEditConTxt(a.t);}} style={{fontSize:'16px'}}>✏️</button>
                        <button onClick={()=>guardar(objs.map(x=>x.id===o.id?{...x, ac:x.ac.filter(y=>y.id!==a.id)}:x))} style={{fontSize:'16px'}}>🗑️</button>
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', justifyContent:'center', marginTop:'4px' }}>
                    {['rojo', 'amarillo', 'verde'].map(est => (
                      <div key={est} onClick={()=>{ guardar(objs.map(x=>x.id===o.id?{...x, ac:x.ac.map(y=>y.id===a.id?{...y, e:est}:y)}:x)); if(est === 'verde') dispararExito(); }} style={{ width:'28px', height:'28px', borderRadius:'50%', backgroundColor: a.e===est?(est==='rojo'?'#EF4444':est==='amarillo'?'#F59E0B':'#10B981'):'#E2E8F0', border: a.e===est?'2px solid #334155':'none', cursor:'pointer' }}></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <RecordatorioModal itemTexto={modalItemTexto} onClose={() => setModalItemTexto(null)} onSave={guardarRecordatorio} isCompartido={!!docUid} color={color} />
      <FooterFirma />
    </div>
  );
}

function MotorProcesosPEC({ titulo, color, bg, storageKey, docUid, collectionAlerts }) {
  const [items, setItems] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [modalItemTexto, setModalItemTexto] = useState(null);
  const targetUid = docUid || auth.currentUser?.uid;

  const [editId, setEditId] = useState(null);
  const [sit, setSit] = useState("");
  const [pens, setPens] = useState([{ id: Date.now(), txt: "", r: null, u: null, d: null, alt: "" }]);
  const [emos, setEmos] = useState([{ id: Date.now() + 1, txt: "", val: null }]);
  const [con, setCon] = useState({ txt: "", p: null, u: null, a: null, alt: "" });

  useEffect(() => {
    const load = async () => {
      if(!targetUid) return;
      const snap = await getDoc(doc(db, storageKey, targetUid));
      if (snap.exists()) setItems(snap.data().pec || []);
    }; load();
  }, [storageKey, targetUid]);

  const guardar = async (nuevos) => { setItems(nuevos); if(targetUid) await setDoc(doc(db, storageKey, targetUid), {pec: nuevos}); };

  const updatePen = (id, field, value) => { setPens(pens.map(p => p.id === id ? { ...p, [field]: value } : p)); };
  const addEmptyPen = () => { setPens([...pens, { id: Date.now(), txt: "", r: null, u: null, d: null, alt: "" }]); };
  const updateEmo = (id, field, value) => { setEmos(emos.map(e => e.id === id ? { ...e, [field]: value } : e)); };
  const addEmptyEmo = () => { setEmos([...emos, { id: Date.now(), txt: "", val: null }]); };

  const hasFalsePen = (p) => p.r === false || p.u === false || p.d === false;
  const hasFalseCon = () => con.p === false || con.u === false || con.a === false;

  const renderFilter = (label, val, setVal) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '13px' }}>
      <span style={{flex:1, paddingRight:'8px'}}>{label}</span>
      <div style={{ display:'flex', gap:'4px' }}>
        <button onClick={()=>setVal(true)} style={{backgroundColor: val===true?'#10B981':'#E2E8F0', color: val===true?'white':'#475569', padding:'4px 10px', borderRadius:'6px', fontWeight:'bold'}}>SÍ</button>
        <button onClick={()=>setVal(false)} style={{backgroundColor: val===false?'#EF4444':'#E2E8F0', color: val===false?'white':'#475569', padding:'4px 10px', borderRadius:'6px', fontWeight:'bold'}}>NO</button>
      </div>
    </div>
  );

  const getEmotionColor = (val) => { if (val === null) return '#E2E8F0'; if (val <= 3) return '#10B981'; if (val <= 7) return '#F59E0B'; return '#EF4444'; };

  const guardarRecordatorio = async (fechasArray, compartir, textoAccion) => {
    const uidToUse = docUid || auth.currentUser?.uid;
    const col = collectionAlerts || "recordatorios_ind"; 
    if (uidToUse) {
      const ref = doc(db, col, uidToUse);
      const snap = await getDoc(ref);
      const data = snap.exists() ? (snap.data().items || []) : [];
      const nuevosItems = fechasArray.map((f, i) => ({ id: Date.now() + i, t: textoAccion, fecha: f, est: 'rojo' }));
      await setDoc(ref, { items: [...nuevosItems, ...data] });
      alert(`¡Programado!`);
    }
  };

  const addPec = () => {
    if(sit.trim() || pens[0].txt.trim() || emos[0].txt.trim() || con.txt.trim()){
      const newPec = { s: sit, p: pens, e: emos, c: con, fecha: new Date().toLocaleDateString('es-ES') };
      if (editId) {
        guardar(items.map(x => x.id === editId ? { ...newPec, id: editId, fecha: x.fecha } : x));
        setEditId(null);
      } else { guardar([{ ...newPec, id: Date.now() }, ...items]); }
      setSit(""); setPens([{ id: Date.now(), txt: "", r: null, u: null, d: null, alt: "" }]); setEmos([{ id: Date.now() + 1, txt: "", val: null }]); setCon({ txt: "", p: null, u: null, a: null, alt: "" });
    }
  };

  const loadToEdit = (it) => {
    setEditId(it.id); setSit(it.s || "");
    setPens(it.p && it.p.length > 0 ? it.p : [{ id: Date.now(), txt: "", r: null, u: null, d: null, alt: "" }]);
    setEmos(Array.isArray(it.e) ? it.e : [{ id: Date.now(), txt: it.e?.txt || "", val: it.e?.val || null }]);
    setCon(it.c || { txt: "", p: null, u: null, a: null, alt: "" });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo={titulo} volverRuta={-1} color={color} />
        
        <button onClick={() => setShowHelp(!showHelp)} style={{ width: '100%', backgroundColor: '#DBEAFE', color: '#1E3A8A', padding: '12px', borderRadius: '12px', fontWeight: 'bold', fontSize: '15px', marginBottom: '15px' }}>
          ℹ️ ¿Cómo rellenar este registro?
        </button>
        
        {showHelp && (
          <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: `1px dashed #1E3A8A`, marginBottom: '20px', fontSize: '13px', color: '#475569', textAlign: 'justify' }}>
            <strong>1. Situación:</strong> Hechos objetivos, como una cámara.<br/>
            <strong>2. Pensamientos:</strong> Qué te dijiste. Evaluaremos si es racional, útil y descriptivo.<br/>
            <strong>3. Emociones:</strong> Qué sentiste e intensidad (0-10).<br/>
            <strong>4. Conductas:</strong> Qué hiciste. Evaluaremos si fue útil.
          </div>
        )}
        
        <div style={{ backgroundColor: editId ? '#FEF9C3' : 'white', padding: '15px', borderRadius: '20px', border: `2px solid ${color}66`, marginBottom: '30px', transition: 'background-color 0.3s' }}>
          <h3 style={{marginTop: 0, color: color, fontSize: '18px'}}>{editId ? "✏️ Editando P-E-C" : "Nuevo registro"}</h3>
          
          <strong style={{color:'#475569', fontSize: '14px'}}>1. SITUACIÓN</strong>
          <AutoTextarea value={sit} onChange={e=>setSit(e.target.value)} placeholder="¿Qué ha pasado?" style={{marginBottom: '15px'}} />
          
          <strong style={{color:'#475569', fontSize: '14px'}}>2. PENSAMIENTOS</strong>
          {pens.map((p, i) => (
            <div key={p.id} style={{ marginBottom: '10px', borderLeft: `3px solid ${color}`, paddingLeft: '8px' }}>
              <AutoTextarea value={p.txt} onChange={e=>updatePen(p.id, 'txt', e.target.value)} placeholder={`Pensamiento ${i+1}`} style={{marginBottom: '5px'}} />
              {p.txt.trim() !== "" && (
                <div style={{ backgroundColor: '#F8FAFC', padding: '10px', borderRadius: '10px', border: '1px solid #E2E8F0', marginTop: '5px' }}>
                  {renderFilter("¿Es racional (hechos)?", p.r, val => updatePen(p.id, 'r', val))}
                  {renderFilter("¿Es útil (me ayuda)?", p.u, val => updatePen(p.id, 'u', val))}
                  {renderFilter("¿Es descriptivo (sin juicios)?", p.d, val => updatePen(p.id, 'd', val))}
                  {hasFalsePen(p) && <AutoTextarea value={p.alt} onChange={e=>updatePen(p.id, 'alt', e.target.value)} placeholder="💡 Pensamiento Alternativo..." style={{ marginTop: '10px', borderColor: '#F59E0B' }} />}
                </div>
              )}
            </div>
          ))}
          <button onClick={addEmptyPen} style={{ color: color, fontWeight: 'bold', fontSize: '14px', marginBottom: '15px', textDecoration: 'underline' }}>➕ Añadir otro pensamiento</button>
          
          <br/><strong style={{color:'#475569', fontSize: '14px'}}>3. EMOCIONES</strong>
          {emos.map((em, i) => (
            <div key={em.id} style={{ marginBottom: '10px', borderLeft: `3px solid ${color}`, paddingLeft: '8px' }}>
              <AutoTextarea value={em.txt} onChange={e=>updateEmo(em.id, 'txt', e.target.value)} placeholder={`Emoción ${i+1}`} style={{marginBottom: '5px'}} />
              <div style={{ fontSize: '12px', color: '#64748B', fontWeight: 'bold', marginBottom: '5px' }}>Intensidad (0-10):</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingBottom: '5px' }}>
                {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} onClick={() => updateEmo(em.id, 'val', n)} style={{ width: '22px', height: '22px', borderRadius: '50%', padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '11px', fontWeight: 'bold', backgroundColor: em.val === n ? getEmotionColor(n) : '#F8FAFC', color: em.val === n ? 'white' : '#475569', border: em.val === n ? 'none' : '1px solid #CBD5E1' }}>{n}</button>
                ))}
              </div>
            </div>
          ))}
          <button onClick={addEmptyEmo} style={{ color: color, fontWeight: 'bold', fontSize: '14px', marginBottom: '15px', textDecoration: 'underline' }}>➕ Añadir otra emoción</button>
          
          <br/><strong style={{color:'#475569', fontSize: '14px'}}>4. CONDUCTAS</strong>
          <div style={{ marginBottom: '10px' }}>
            <AutoTextarea value={con.txt} onChange={e=>setCon({...con, txt: e.target.value})} placeholder="¿Qué hiciste finalmente?" style={{marginBottom: '5px'}} />
            {con.txt.trim() !== "" && (
              <div style={{ backgroundColor: '#F8FAFC', padding: '10px', borderRadius: '10px', border: '1px solid #E2E8F0', marginTop: '5px' }}>
                {renderFilter("¿Conducta positiva?", con.p, val => setCon({...con, p: val}))}
                {renderFilter("¿Útil a largo plazo?", con.u, val => setCon({...con, u: val}))}
                {renderFilter("¿Ha sido adaptativa?", con.a, val => setCon({...con, a: val}))}
                {hasFalseCon() && (
                  <div style={{ marginTop: '10px' }}>
                    <AutoTextarea value={con.alt} onChange={e=>setCon({...con, alt: e.target.value})} placeholder="🔄 Conducta Alternativa..." style={{ borderColor: '#F59E0B', marginBottom: '8px' }} />
                    {con.alt.trim() !== "" && <button onClick={()=>setModalItemTexto(con.alt)} style={{ backgroundColor: '#10B981', color: 'white', padding: '8px', borderRadius: '8px', fontWeight: 'bold', width: '100%', fontSize: '13px' }}>🔔 Enviar al Tablón</button>}
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={addPec} style={{backgroundColor:color, color:'white', padding:'12px', borderRadius:'10px', fontWeight:'800', width: '100%', fontSize: '15px', marginTop: '10px'}}>
            {editId ? "ACTUALIZAR" : "GUARDAR"}
          </button>
          {editId && <button onClick={()=>{ setEditId(null); setSit(""); setPens([{ id: Date.now(), txt: "", r: null, u: null, d: null, alt: "" }]); setEmos([{ id: Date.now() + 1, txt: "", val: null }]); setCon({ txt: "", p: null, u: null, a: null, alt: "" }); }} style={{backgroundColor:'#E2E8F0', color:'#475569', padding:'12px', borderRadius:'10px', fontWeight:'800', width: '100%', fontSize: '15px', marginTop: '8px'}}>CANCELAR EDICIÓN</button>}
        </div>

        {items.map((it) => (
          <div key={it.id} style={{ backgroundColor: 'white', padding: '15px', borderRadius: '16px', border: `2px solid ${color}22`, marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
              <span style={{ color: '#64748B', fontWeight: '700', fontSize: '14px' }}>📅 {it.fecha}</span>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button onClick={()=>loadToEdit(it)} style={{fontSize:'16px'}}>✏️</button>
                <button onClick={()=>{if(window.confirm("¿Borrar?")) guardar(items.filter(x=>x.id!==it.id));}} style={{fontSize:'16px'}}>🗑️</button>
              </div>
            </div>
            {it.s && <div style={{marginBottom:'10px', fontSize:'13px'}}><strong>SITUACIÓN:</strong><br/><span style={{color:'#475569'}}>{it.s}</span></div>}
            {(it.p || []).length > 0 && <div style={{marginBottom:'10px', fontSize:'13px'}}><strong>PENSAMIENTOS:</strong>
              {(it.p || []).map((pen, idx) => (
                <div key={idx} style={{ marginTop: '4px', paddingLeft: '8px', borderLeft: '2px solid #CBD5E1' }}>
                  <span style={{color:'#475569'}}>{pen.txt}</span>
                  {pen.alt && <div style={{color:'#D97706', fontSize:'12px', marginTop:'2px'}}>💡 Alt: {pen.alt}</div>}
                </div>
              ))}
            </div>}
            {it.e && <div style={{marginBottom:'10px', fontSize:'13px'}}><strong>EMOCIONES:</strong>
              {Array.isArray(it.e) ? ( it.e.map((em, idx) => (
                <div key={idx} style={{ marginTop: '4px', paddingLeft: '8px', borderLeft: '2px solid #CBD5E1' }}>
                  <span style={{color:'#475569'}}>{em.txt}</span>
                  {em.val !== null && <span style={{ marginLeft: '8px', backgroundColor: getEmotionColor(em.val), color: 'white', padding: '2px 6px', borderRadius: '6px', fontWeight: 'bold', fontSize: '11px' }}>{em.val}</span>}
                </div>
              ))) : (
                <div style={{ marginTop: '4px', paddingLeft: '8px', borderLeft: '2px solid #CBD5E1' }}>
                  <span style={{color:'#475569'}}>{typeof it.e === 'string' ? it.e : (it.e?.txt || "")}</span>
                  {typeof it.e === 'object' && it.e?.val !== null && <span style={{ marginLeft: '8px', backgroundColor: getEmotionColor(it.e.val), color: 'white', padding: '2px 6px', borderRadius: '6px', fontWeight: 'bold', fontSize: '11px' }}>{it.e.val}</span>}
                </div>
              )}
            </div>}
            {it.c?.txt && <div style={{marginBottom:'5px', fontSize:'13px'}}><strong>CONDUCTA:</strong><br/><span style={{color:'#475569'}}>{it.c.txt}</span>
              {it.c.alt && <div style={{color:'#10B981', fontSize:'12px', marginTop:'4px'}}>🔄 Alt: {it.c.alt}</div>}
            </div>}
          </div>
        ))}
      </div>
      <RecordatorioModal itemTexto={modalItemTexto} onClose={() => setModalItemTexto(null)} onSave={guardarRecordatorio} isCompartido={!!docUid} color={color} />
      <FooterFirma />
    </div>
  );
}

// ----------------- VISTAS DE MÓDULOS INDIVIDUALES, PAREJA Y FAMILIA -----------------

function MiRincon() {
  const navigate = useNavigate();
  const c = "#1E3A8A"; 
  const btnLargo = { width: '100%', backgroundColor: '#ffffff', border: `2px solid ${c}66`, borderRadius: '16px', color: c, padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '10px', fontSize: '18px', fontWeight: '700' };
  const btnLargoFuerte = { ...btnLargo, backgroundColor: c, color: 'white' };
  const btnGrid = { backgroundColor: '#ffffff', border: `2px solid ${c}66`, borderRadius: '16px', color: c, padding: '12px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '15px', fontWeight: '700', textAlign: 'center', minHeight: '100px' };
  const btnCirc = { width: '140px', height: '140px', borderRadius: '50%', backgroundColor: '#ffffff', border: `2px solid ${c}66`, color: c, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', fontSize: '15px', fontWeight: '800', textAlign: 'center', lineHeight: '1.2' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <div style={{ flex: 1, padding: '15px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ maxWidth: '600px', width: '100%', backgroundColor: '#ffffff', padding: '20px 15px', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
          <HeaderSeccion titulo="MI RINCÓN PERSONAL" volverRuta="/" color={c} />
          
          <button onClick={() => navigate('/recordatorios-individual')} style={btnLargoFuerte}><IconCampana color="white" /> TABLÓN DE AVISOS</button>
          {/* BOTÓN EVALUACIÓN QUINCENAL VOLUNTARIA */}
          
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <button onClick={() => navigate('/mi-autoestima')} style={btnGrid}><IconEstrella color={c} /> MI AUTOESTIMA</button>
            <button onClick={() => navigate('/mis-valores')} style={btnGrid}><IconBrujula color={c} /> MIS VALORES</button>
            <button onClick={() => navigate('/objetivos-individuales')} style={btnGrid}><IconGrafico color={c} /> MIS OBJETIVOS</button>
            <button onClick={() => navigate('/procesos-pec-individual')} style={btnGrid}><IconCerebro color={c} /> MIS PROCESOS P-E-C</button>
          </div>
          
          <button onClick={() => navigate('/evaluacion-quincenal')} style={btnLargo}><IconDiana color={c} /> MI EVALUACIÓN QUINCENAL</button>
          
          <div style={{ display: 'flex', gap: '15px', marginTop: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/baul-individual')} style={btnCirc}><IconCaja color={c} /> EL BAÚL<br/>DE ENREDOS</button>
            <button onClick={() => navigate('/luz-individual')} style={btnCirc}><IconSol color={c} /> HACIENDO MI<br/>MUNDO BONITO</button>
          </div>
        </div>
      </div>
      <FooterFirma />
    </div>
  );
}

// ----------------- MÓDULO PAREJA: MOVIÉNDONOS PARA ESTAR MEJOR (ESTILO INDIVIDUAL + COMÚN) -----------------
function MoviendonosPareja({ docUid, userData }) {
  const navigate = useNavigate();
  const [objs, setObjs] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [modalItemTexto, setModalItemTexto] = useState(null);
  const [showExito, setShowExito] = useState(false);
  const [editObjId, setEditObjId] = useState(null);
  const [editObjTxt, setEditObjTxt] = useState("");
  const [editConId, setEditConId] = useState(null);
  const [editConTxt, setEditConTxt] = useState("");
  
  const targetUid = docUid; // Es el sharedId de la pareja
  const c = "#B91C1C"; // Rojo para pareja

  useEffect(() => {
    if(!targetUid) return;
    const unsub = onSnapshot(doc(db, "parejas_comun", targetUid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setObjs(data.objs || []);
        
        // Si hay cambios de la pareja, marcar como visto al entrar
        if (data.editadoPor && data.editadoPor !== auth.currentUser?.uid && data.visto === false) {
          updateDoc(doc(db, "parejas_comun", targetUid), { visto: true });
        }
      }
    });
    return () => unsub();
  }, [targetUid]);

  const dispararExito = () => { setShowExito(true); setTimeout(() => setShowExito(false), 3000); };

  const guardar = async (nuevos) => {
    setObjs(nuevos);
    if(targetUid) {
      await setDoc(doc(db, "parejas_comun", targetUid), {
        objs: nuevos,
        editadoPor: auth.currentUser.uid,
        editadoPorNombre: userData?.alias || "Tu pareja",
        fechaEdicion: Date.now(),
        visto: false // Al guardar algo, la pareja verá el aviso de "NUEVO"
      }, { merge: true });
    }
  };

  const addObj = () => { if (nuevo.trim()) { guardar([{ id: Date.now(), t: nuevo, ac: [] }, ...objs]); setNuevo(""); } };

  const guardarRecordatorio = async (fechasArray, compartir, textoAccion) => {
    if (targetUid) {
      const ref = doc(db, "recordatorios_par", targetUid);
      const snap = await getDoc(ref);
      const data = snap.exists() ? (snap.data().items || []) : [];
      const nuevosItems = fechasArray.map((f, i) => ({ id: Date.now() + i, t: textoAccion, fecha: f, est: null }));
      await setDoc(ref, { items: [...nuevosItems, ...data] });
      alert(`¡Programado en el tablón compartido!`);
    }
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: '#FEF2F2', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ToastExito mostrar={showExito} />
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <HeaderSeccion titulo="MOVIÉNDONOS PARA ESTAR MEJOR" volverRuta="/nuestro-espacio" color={c} />
        
        <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: `1px dashed ${c}44`, marginBottom: '15px', fontSize: '13px', color: '#475569', textAlign: 'justify' }}>
          Este es vuestro espacio compartido para definir <strong>Objetivos y Conductas comunes</strong>. Todo lo que creéis aquí es visible y editable por ambos. Dale a la campana (🔔) para enviarlo al tablón compartido.
        </div>

        <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '16px', border: `2px solid ${c}`, display: 'flex', gap: '10px', marginBottom: '25px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}><AutoTextarea value={nuevo} onChange={e=>setNuevo(e.target.value)} placeholder="Ej: Mejorar nuestra comunicación..." /></div>
          <button onClick={addObj} style={{backgroundColor:c, color:'white', padding:'10px', borderRadius:'10px', fontWeight:'700', fontSize:'13px'}}>CREAR</button>
        </div>

        {objs.map((o) => (
          <div key={o.id} style={{ backgroundColor: 'white', padding: '12px', borderRadius: '16px', border: `2px solid ${c}22`, marginBottom: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', marginBottom: '12px' }}>
              {editObjId === o.id ? (
                <div style={{ display:'flex', flex:1, gap:'8px' }}>
                  <AutoTextarea value={editObjTxt} onChange={e=>setEditObjTxt(e.target.value)} style={{flex:1, margin:0}} />
                  <button onClick={()=>{guardar(objs.map(x=>x.id===o.id?{...x, t:editObjTxt}:x)); setEditObjId(null);}} style={{fontSize:'18px'}}>💾</button>
                </div>
              ) : (
                <>
                  <h3 style={{ margin: 0, fontSize: '16px', color: c, flex:1 }}>{o.t}</h3>
                  <div style={{ display:'flex', gap:'5px' }}>
                    <button onClick={()=>{setEditObjId(o.id); setEditObjTxt(o.t);}} style={{fontSize:'16px'}}>✏️</button>
                    <button onClick={()=>{if(window.confirm("¿Borrar objetivo compartido?")) guardar(objs.filter(x=>x.id!==o.id));}} style={{fontSize:'16px'}}>🗑️</button>
                  </div>
                </>
              )}
            </div>
            
            <ConductaInput customPlaceholder="Añadir conducta compartida..." onAdd={(txt) => guardar(objs.map(x=>x.id===o.id?{...x, ac:[{id:Date.now(), t:txt, e:'rojo'}, ...(x.ac||[])]}:x))} />

            <div style={{ marginTop: '10px' }}>
              {(o.ac || []).map(a => (
                <div key={a.id} style={{ padding: '8px', backgroundColor: '#F8FAFC', borderRadius: '10px', marginBottom: '6px', display:'flex', flexDirection:'column', gap:'8px', border: '1px solid #E2E8F0' }}>
                  {editConId === a.id ? (
                    <div style={{ display:'flex', gap:'8px' }}>
                      <AutoTextarea value={editConTxt} onChange={e=>setEditConTxt(e.target.value)} style={{flex:1, margin:0}} />
                      <button onClick={()=>{guardar(objs.map(x=>x.id===o.id?{...x, ac:x.ac.map(y=>y.id===a.id?{...y, t:editConTxt}:y)}:x)); setEditConId(null);}} style={{fontSize:'18px'}}>💾</button>
                    </div>
                  ) : (
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <span style={{fontSize:'14px', flex:1, paddingRight:'8px'}}>{a.t}</span>
                      <div style={{ display:'flex', gap:'5px' }}>
                        <button onClick={()=>setModalItemTexto(a.t)} style={{fontSize:'16px'}} title="Al Tablón">🔔</button>
                        <button onClick={()=>{setEditConId(a.id); setEditConTxt(a.t);}} style={{fontSize:'16px'}}>✏️</button>
                        <button onClick={()=>guardar(objs.map(x=>x.id===o.id?{...x, ac:x.ac.filter(y=>y.id!==a.id)}:x))} style={{fontSize:'16px'}}>🗑️</button>
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', justifyContent:'center', marginTop:'4px' }}>
                    {['rojo', 'amarillo', 'verde'].map(est => (
                      <div key={est} onClick={()=>{ guardar(objs.map(x=>x.id===o.id?{...x, ac:x.ac.map(y=>y.id===a.id?{...y, e:est}:y)}:x)); if(est === 'verde') dispararExito(); }} style={{ width:'28px', height:'28px', borderRadius:'50%', backgroundColor: a.e===est?(est==='rojo'?'#EF4444':est==='amarillo'?'#F59E0B':'#10B981'):'#E2E8F0', border: a.e===est?'2px solid #334155':'none', cursor:'pointer' }}></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <RecordatorioModal itemTexto={modalItemTexto} onClose={() => setModalItemTexto(null)} onSave={guardarRecordatorio} isCompartido={true} color={c} />
      <FooterFirma />
    </div>
  );
}

// ----------------- MÓDULO CUIDANDO EL NOSOTROS (RENOVADO - CUADRADOS TRÍO SIMÉTRICO) -----------------
function NuestroEspacio({ userData, setUserData, sharedId }) {
  const navigate = useNavigate();
  const [codigoInput, setCodigoInput] = useState("");
  const [mostrarVinculacion, setMostrarVinculacion] = useState(() => sessionStorage.getItem('skipPar') !== 'true'); 
  const [tieneCambios, setTieneCambios] = useState(false);
  const c = "#B91C1C";

  useEffect(() => {
    if(!sharedId) return;
    const unsub = onSnapshot(doc(db, "parejas_comun", sharedId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.editadoPor && data.editadoPor !== auth.currentUser?.uid && data.visto === false) {
          setTieneCambios(true);
        } else {
          setTieneCambios(false);
        }
      }
    });
    return () => unsub();
  }, [sharedId]);

  const vincularPareja = async () => {
    if(!codigoInput.trim() || codigoInput === userData?.nexoCode) return alert("Código no válido.");
    try {
      const q = query(collection(db, "users"), where("nexoCode", "==", codigoInput.toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) return alert("Pareja no encontrada.");
      const partnerId = snap.docs[0].id;
      await updateDoc(doc(db, "users", auth.currentUser.uid), { partnerUid: partnerId });
      await updateDoc(doc(db, "users", partnerId), { partnerUid: auth.currentUser.uid });
      setUserData({...userData, partnerUid: partnerId});
      sessionStorage.removeItem('skipPar');
      alert("¡Vinculación completada!");
    } catch(err) { alert("Error al vincular."); }
  };

  // ESTILOS UNIFICADOS PARA LOS TRES CUADRADOS (Letra más grande)
  const btnCuadrado = { 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center', 
    backgroundColor: '#ffffff', 
    border: `2px solid ${c}66`, 
    borderRadius: '20px', 
    color: c, 
    padding: '20px 10px', 
    gap: '15px', 
    fontSize: '18px', // Subido a 18px
    fontWeight: '800', 
    textAlign: 'center',
    minHeight: '170px', // Un poco más de margen para la letra grande
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    boxSizing: 'border-box'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#FEF2F2' }}>
      <div style={{ flex: 1, padding: '15px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ maxWidth: '600px', width: '100%', backgroundColor: '#ffffff', padding: '20px 15px', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
          <HeaderSeccion titulo="CUIDANDO EL NOSOTROS" volverRuta="/" color={c} />
          
          {!userData?.partnerUid && mostrarVinculacion ? (
            <div style={{ textAlign: 'center', padding: '15px' }}>
              <IconVincular color={c} />
              <h2 style={{ color: c, fontSize: '20px' }}>VINCULACIÓN</h2>
              <div style={{ backgroundColor: '#FEE2E2', padding: '15px', borderRadius: '15px', margin: '20px 0', border: `2px dashed ${c}` }}>
                <span style={{ fontSize: '14px', color: '#7F1D1D' }}>Tu código para compartir:</span>
                <strong style={{ fontSize: '30px', letterSpacing: '4px', color: c, display: 'block' }}>{userData?.nexoCode || "..."}</strong>
              </div>
              <input type="text" placeholder="Código de tu pareja" value={codigoInput} onChange={e=>setCodigoInput(e.target.value)} style={{ textAlign: 'center', marginBottom: '15px' }} />
              <button onClick={vincularPareja} style={{ backgroundColor: c, color: 'white', width: '100%', padding: '15px', fontSize: '16px', borderRadius: '12px', fontWeight: '800', marginBottom: '10px' }}>UNIR NUESTRAS CUENTAS</button>
              <button onClick={() => { sessionStorage.setItem('skipPar', 'true'); setMostrarVinculacion(false); }} style={{ backgroundColor: '#E2E8F0', color: '#475569', width: '100%', padding: '12px', fontSize: '14px', borderRadius: '12px', fontWeight: '700' }}>ENTRAR SIN VINCULAR</button>
            </div>
          ) : (
            <>
              {!userData?.partnerUid && <div style={{ backgroundColor: '#F1F5F9', padding: '8px', borderRadius: '10px', textAlign: 'center', marginBottom: '15px' }}><button onClick={() => { sessionStorage.removeItem('skipPar'); setMostrarVinculacion(true); }} style={{ color: c, textDecoration: 'underline', fontWeight: '700', fontSize: '13px' }}>Vincular cuenta ahora</button></div>}
              
              {/* GRID DE CUADRADOS SIMÉTRICO */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                
                {/* 1. Tablón */}
                <button 
                  onClick={() => navigate('/recordatorios-pareja')} 
                  style={btnCuadrado}
                >
                  <div style={{ transform: 'scale(1.5)' }}><IconCampana color={c} /></div>
                  TABLÓN COMPARTIDO
                </button>

                {/* 2. Objetivos y Conductas */}
                <button 
                  onClick={() => navigate('/moviendonos-pareja')} 
                  style={{ 
                    ...btnCuadrado, 
                    backgroundColor: tieneCambios ? '#EF4444' : '#ffffff',
                    color: tieneCambios ? 'white' : c,
                    borderColor: tieneCambios ? '#EF4444' : `${c}66`
                  }}
                >
                  <div style={{ transform: 'scale(1.5)' }}><IconGrafico color={tieneCambios ? 'white' : c} /></div>
                  NUESTROS OBJETIVOS Y CONDUCTAS {tieneCambios && <span style={{fontSize:'12px', display:'block'}}>(¡NUEVO!)</span>}
                </button>

                {/* 3. Fortalezas */}
                <button 
                  onClick={() => navigate('/fortalezas-pareja')} 
                  style={{ 
                    ...btnCuadrado, 
                    gridColumn: 'span 2', 
                    justifySelf: 'center', 
                    width: 'calc(50% - 7.5px)' 
                  }}
                >
                  <div style={{ transform: 'scale(1.5)' }}><IconEscudo color={c} /></div>
                  NUESTRAS FORTALEZAS
                </button>

              </div>
            </>
          )}
        </div>
      </div>
      <FooterFirma />
    </div>
  );
}

function EspacioFamiliar({ userData, setUserData, familyId }) {
  const navigate = useNavigate();
  const [codigoInput, setCodigoInput] = useState("");
  const [mostrarVinculacion, setMostrarVinculacion] = useState(() => sessionStorage.getItem('skipFam') !== 'true'); 
  const cV = "#047857";

  const crearFamilia = async () => {
    try {
      const newFamilyId = "FAM-" + Math.random().toString(36).substring(2, 7).toUpperCase();
      await updateDoc(doc(db, "users", auth.currentUser.uid), { familyGroupId: newFamilyId });
      setUserData({...userData, familyGroupId: newFamilyId});
      sessionStorage.removeItem('skipFam');
      alert("¡Sala Familiar creada!");
    } catch(err) { alert("Error al crear."); }
  };
  const unirseFamilia = async () => {
    if(!codigoInput.trim() || !codigoInput.toUpperCase().startsWith("FAM-")) return alert("Código inválido.");
    try {
      await updateDoc(doc(db, "users", auth.currentUser.uid), { familyGroupId: codigoInput.toUpperCase() });
      setUserData({...userData, familyGroupId: codigoInput.toUpperCase()});
      sessionStorage.removeItem('skipFam');
      alert("¡Te has unido a la Sala Familiar!");
    } catch(err) { alert("Error al vincular."); }
  };

  const btnGrid = { backgroundColor: '#ffffff', border: `2px solid ${cV}66`, borderRadius: '16px', color: cV, padding: '12px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '15px', fontWeight: '700', textAlign: 'center', minHeight: '100px' };
  const btnLargo = { position: 'relative', width: '100%', backgroundColor: '#ffffff', border: `2px solid ${cV}66`, borderRadius: '16px', color: cV, padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '10px', fontSize: '18px', fontWeight: '700' };
  const btnLargoFuerte = { ...btnLargo, backgroundColor: cV, color: 'white' };
  const btnCirc = { width: '140px', height: '140px', borderRadius: '50%', backgroundColor: '#ffffff', border: `2px solid ${cV}66`, color: cV, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', fontSize: '15px', fontWeight: '800', textAlign: 'center', lineHeight: '1.2' };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#ECFDF5' }}>
      <div style={{ flex: 1, padding: '15px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ maxWidth: '600px', width: '100%', backgroundColor: '#ffffff', padding: '20px 15px', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
          <HeaderSeccion titulo="CUIDANDO LA FAMILIA" volverRuta="/" color={cV} />
          {!userData?.familyGroupId && mostrarVinculacion ? (
            <div style={{ textAlign: 'center', padding: '15px' }}>
              <IconFamilia color={cV} />
              <h2 style={{ color: cV, fontSize:'20px' }}>SALA FAMILIAR</h2>
              <button onClick={crearFamilia} style={{ backgroundColor: cV, color: 'white', width: '100%', padding: '15px', fontSize: '16px', borderRadius: '12px', fontWeight: '800', marginTop: '15px', marginBottom: '20px' }}>+ CREAR NUEVA FAMILIA</button>
              <input type="text" placeholder="Ej: FAM-A7X9" value={codigoInput} onChange={e=>setCodigoInput(e.target.value)} style={{ textAlign: 'center', marginBottom: '15px' }} />
              <button onClick={unirseFamilia} style={{ backgroundColor: '#10B981', color: 'white', width: '100%', padding: '15px', fontSize: '16px', borderRadius: '12px', fontWeight: '800', marginBottom: '10px' }}>ENTRAR A LA SALA</button>
              <button onClick={() => { sessionStorage.setItem('skipFam', 'true'); setMostrarVinculacion(false); }} style={{ backgroundColor: '#E2E8F0', color: '#475569', width: '100%', padding: '12px', fontSize: '14px', borderRadius: '12px', fontWeight: '700' }}>ENTRAR SIN VINCULAR</button>
            </div>
          ) : (
            <>
              {!userData?.familyGroupId && <div style={{ backgroundColor: '#F1F5F9', padding: '8px', borderRadius: '10px', textAlign: 'center', marginBottom: '15px' }}><button onClick={() => { sessionStorage.removeItem('skipFam'); setMostrarVinculacion(true); }} style={{ color: cV, textDecoration: 'underline', fontWeight: '700', fontSize:'13px' }}>Vincular familia ahora</button></div>}
              <button onClick={() => navigate('/recordatorios-familia')} style={btnLargoFuerte}><IconCampana color="white" /> TABLÓN FAMILIAR</button>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <button onClick={() => navigate('/objetivos-familia')} style={btnGrid}><IconGrafico color={cV} /> OBJETIVOS</button>
                <button onClick={() => navigate('/fortalezas-familia')} style={btnGrid}><IconEscudo color={cV} /> FORTALEZAS</button>
              </div>
<div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                <button onClick={() => navigate('/procesos-pec-familia')} style={{ ...btnGrid, width: 'calc(50% - 6px)' }}><IconCerebro color={cV} /> P-E-C FAMILIAR</button>
              </div>
              <div style={{ display: 'flex', gap: '15px', marginTop: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/baul-familia')} style={btnCirc}><IconCaja color={cV} /> EL BAÚL<br/>DE ENREDOS</button>
                <button onClick={() => navigate('/luz-familia')} style={btnCirc}><IconSol color={cV} /> CUIDANDO<br/>LA FAMILIA</button>
              </div>
            </>
          )}
        </div>
      </div>
      <FooterFirma />
    </div>
  );
}

// ----------------- PANTALLA NORMAS -----------------
function NormasUso() {
  const c = "#1E3A8A";
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <div style={{ flex: 1, padding: '15px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ maxWidth: '800px', width: '100%', backgroundColor: '#ffffff', padding: '20px 15px', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
          <HeaderSeccion titulo="NORMAS Y PROTOCOLO" volverRuta="/" color={c} />
          <div style={{ fontSize: '15px', color: '#475569', lineHeight: '1.5', textAlign: 'justify' }}>
            <h3 style={{ color: c, textAlign: 'center', marginBottom: '20px', fontSize: '20px' }}>BIENVENIDO A TU HERRAMIENTA</h3>
            <p>Te presento una nueva aplicación que he diseñado exclusivamente para acompañarte en tu día a día, para que tú mismo vayas gestionando tu camino. Es un lugar totalmente privado y seguro, instalado en tu propio teléfono, para que puedas reflexionar, anotar y avanzar a tu ritmo. El objetivo con el uso de esta aplicación estructurada es facilitar que paso a paso, poco a poco los cambios lleguen y acaben convirtiéndose en hábitos positivos.</p>
            <h3 style={{ color: c, marginTop: '20px' }}>Tu ritmo y tu espacio</h3>
            <p>Entra cuando necesites organizarte, registrar ideas o recordatorios. Mi consejo es que le dediques unos minutos cada día. Encontrarás un Buzón de Sugerencias. Estaré encantado de que compartas cualquier idea.</p>
            <h3 style={{ color: c, marginTop: '20px' }}>Normas de Uso y Privacidad</h3>
            <ul style={{ paddingLeft: '20px' }}>
              <li style={{ marginBottom: '10px' }}><strong>Uso clínico:</strong> La app sirve para registrar tu evolución. No debe utilizarse para comunicar emergencias.</li>
              <li style={{ marginBottom: '10px' }}><strong>Tu acceso:</strong> Acceso total activo hasta dos meses después de nuestra última sesión de alta.</li>
              <li style={{ marginBottom: '10px' }}><strong>Derecho al olvido:</strong> Finalizada la relación profesional, tus datos serán borrados. Puedes solicitarlo en psicologofranciscobeltran@hotmail.com.</li>
            </ul>
            <p style={{ marginTop: '30px', fontSize: '16px', textAlign: 'center' }}>
              Un saludo,<br/><strong style={{ color: c }}>Francisco Beltrán.</strong>
            </p>
          </div>
        </div>
      </div>
      <FooterFirma />
    </div>
  );
}

// ----------------- PANEL ADMINISTRADOR Y FICHA PACIENTE -----------------
function AdminPanel() {
  const [usuarios, setUsuarios] = useState([]);
  const [frasesInd, setFrasesInd] = useState([]);
  const [frasesComp, setFrasesComp] = useState([]);
  const [nuevaFrase, setNuevaFrase] = useState("");
  const [textoMasivo, setTextoMasivo] = useState("");
  const [categoria, setCategoria] = useState("frases_diarias");
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const usersSnap = await getDocs(collection(db, "users"));
      setUsuarios(usersSnap.docs.map(d=>({id: d.id, ...d.data()})));
      const fIndSnap = await getDocs(collection(db, "frases_diarias"));
      setFrasesInd(fIndSnap.docs.map(d=>({id: d.id, ...d.data()})));
      const fCompSnap = await getDocs(collection(db, "frases_compartidas"));
      setFrasesComp(fCompSnap.docs.map(d=>({id: d.id, ...d.data()})));
    }; load();
  }, []);

  const subirFrasesMasivas = async () => {
    if (!textoMasivo.trim()) return;
    const lineas = textoMasivo.split('\n').filter(l => l.trim() !== "");
    if (lineas.length === 0) return;
    if (window.confirm(`¿Subir ${lineas.length} frases a ${categoria}?`)) {
      try {
        const batch = writeBatch(db);
        lineas.forEach(txt => { batch.set(doc(collection(db, categoria)), { texto: txt.trim(), createdAt: Date.now() }); });
        await batch.commit(); alert("¡Subida completada!"); setTextoMasivo(""); window.location.reload();
      } catch (e) { alert("Error."); }
    }
  };

  const addFrase = async () => {
    if(!nuevaFrase.trim()) return;
    try {
      const docRef = await addDoc(collection(db, categoria), { texto: nuevaFrase, createdAt: Date.now() });
      if(categoria === 'frases_diarias') setFrasesInd([{id: docRef.id, texto: nuevaFrase}, ...frasesInd]);
      else setFrasesComp([{id: docRef.id, texto: nuevaFrase}, ...frasesComp]);
      setNuevaFrase(""); alert("Añadida.");
    } catch (e) { alert("Error."); }
  };
  const deleteFrase = async (id, col) => {
    if(window.confirm("¿Borrar?")) {
      await deleteDoc(doc(db, col, id));
      if(col === 'frases_diarias') setFrasesInd(frasesInd.filter(f => f.id !== id));
      else setFrasesComp(frasesComp.filter(f => f.id !== id));
    }
  };

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <GlobalStyles />
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <HeaderSeccion titulo="Panel Clínico" volverRuta="/" color="#1E293B" />
        
        <h3 style={{ color: '#1E3A8A', marginTop: '30px' }}>👥 Pacientes Registrados ({usuarios.length})</h3>
        <div style={{ display: 'grid', gap: '10px', marginBottom: '30px' }}>
          {usuarios.map(u => (
            <div key={u.id} style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: '1px solid #CBD5E1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{fontSize:'16px'}}>{u.alias || "Sin Alias"}</strong>
                <div style={{ fontSize: '13px', color: '#64748B' }}>Cita: {u.proximaCita || 'No fijada'}</div>
              </div>
              <button onClick={() => navigate(`/admin/paciente/${u.id}/${u.alias || 'Paciente'}`)} style={{ backgroundColor: '#1E3A8A', color: 'white', padding: '8px 12px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                👁️ VER FICHA
              </button>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: '#E0E7FF', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '2px solid #4338CA', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#4338CA', fontSize:'16px' }}>📌 Destino de Frases:</h3>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: 'bold' }}><input type="radio" value="frases_diarias" checked={categoria === 'frases_diarias'} onChange={()=>setCategoria('frases_diarias')}/> Indiv ({frasesInd.length})</label>
            <label style={{ fontSize: '14px', fontWeight: 'bold' }}><input type="radio" value="frases_compartidas" checked={categoria === 'frases_compartidas'} onChange={()=>setCategoria('frases_compartidas')}/> Pareja ({frasesComp.length})</label>
          </div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '2px solid #1E3A8A', marginBottom: '20px' }}>
          <h3 style={{ color: '#1E3A8A', marginTop: 0, fontSize:'16px' }}>🚀 SUBIDA MASIVA</h3>
          <textarea value={textoMasivo} onChange={e=>setTextoMasivo(e.target.value)} placeholder="Una frase por línea..." style={{ minHeight: '100px', fontSize: '14px', width: '100%', padding: '10px' }} />
          <button onClick={subirFrasesMasivas} style={{ backgroundColor: '#1E3A8A', color: 'white', padding: '10px', borderRadius: '8px', width: '100%', marginTop: '10px', fontWeight: 'bold' }}>CARGAR LISTA</button>
        </div>
      </div>
    </div>
  );
}

function FichaPacienteAdmin() {
  const { id, nombre } = useParams();
  const [data, setData] = useState({ eval: null, objs: [], pec: [] });

  useEffect(() => {
    const fetchDatos = async () => {
      const eSnap = await getDoc(doc(db, "evaluaciones", id));
      const oSnap = await getDoc(doc(db, "obj_ind", id));
      const pSnap = await getDoc(doc(db, "pec_ind", id));
      setData({
        eval: eSnap.exists() ? eSnap.data() : null,
        objs: oSnap.exists() ? oSnap.data().objs || [] : [],
        pec: pSnap.exists() ? pSnap.data().pec || [] : []
      });
    }; fetchDatos();
  }, [id]);

  const getCol = (v) => v <= 3 ? '#EF4444' : v <= 7 ? '#F59E0B' : '#10B981';

  return (
    <div style={{ padding: '15px 10px', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <HeaderSeccion titulo={`Ficha: ${nombre}`} volverRuta="/admin" color="#1E3A8A" />
        
        <h3 style={{ color: '#047857', borderBottom: '2px solid #047857', paddingBottom: '5px' }}>Rueda de la Vida (Última)</h3>
        {data.eval ? (
          <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: '1px solid #CBD5E1', marginBottom: '20px' }}>
            <span style={{ fontSize: '12px', color: '#64748B' }}>Fecha: {new Date(data.eval.fecha).toLocaleDateString()}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
              {Object.keys(data.eval.rueda || {}).map(k => (
                <div key={k} style={{ backgroundColor: '#F1F5F9', padding: '5px 10px', borderRadius: '8px', fontSize: '13px' }}>
                  <strong>{k}:</strong> <span style={{ color: getCol(data.eval.rueda[k]), fontWeight: 'bold' }}>{data.eval.rueda[k]}</span>
                </div>
              ))}
            </div>
          </div>
        ) : <p style={{ fontSize: '13px', color: '#64748B' }}>No hay evaluación registrada.</p>}

        <h3 style={{ color: '#1E3A8A', borderBottom: '2px solid #1E3A8A', paddingBottom: '5px' }}>Objetivos y Conductas Activos</h3>
        {data.objs.length > 0 ? data.objs.map(o => (
          <div key={o.id} style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: '1px solid #CBD5E1', marginBottom: '10px' }}>
            <strong style={{ color: '#1E3A8A', fontSize: '15px' }}>{o.t}</strong>
            <ul style={{ paddingLeft: '20px', margin: '10px 0 0 0', fontSize: '13px' }}>
              {(o.ac || []).map(a => <li key={a.id} style={{ color: '#475569' }}>{a.t}</li>)}
            </ul>
          </div>
        )) : <p style={{ fontSize: '13px', color: '#64748B' }}>No hay objetivos registrados.</p>}

        <h3 style={{ color: '#B91C1C', borderBottom: '2px solid #B91C1C', paddingBottom: '5px' }}>Últimos Procesos P-E-C</h3>
        {data.pec.length > 0 ? data.pec.slice(0,5).map(p => (
          <div key={p.id} style={{ backgroundColor: 'white', padding: '15px', borderRadius: '12px', border: '1px solid #CBD5E1', marginBottom: '10px', fontSize: '13px' }}>
            <div style={{ color: '#64748B', fontWeight: 'bold', marginBottom: '5px' }}>📅 {p.fecha}</div>
            {p.s && <div><strong>SIT:</strong> {p.s}</div>}
            {p.p && p.p[0]?.txt && <div style={{ marginTop: '5px' }}><strong>PEN:</strong> {p.p[0].txt}</div>}
            {p.c && p.c.txt && <div style={{ marginTop: '5px' }}><strong>CON:</strong> {p.c.txt}</div>}
          </div>
        )) : <p style={{ fontSize: '13px', color: '#64748B' }}>No hay procesos P-E-C registrados.</p>}
      </div>
    </div>
  );
}

// ----------------- PANTALLA PRINCIPAL DE INICIO -----------------
function Inicio({ userData, setUserData }) {
  const navigate = useNavigate();
  const [cita, setCita] = useState(userData?.proximaCita || "");

  // --- INICIO DEL PORTERO (15 DÍAS) ---
  const quinceDias = 15 * 24 * 60 * 60 * 1000;
  const necesitaEval = !userData?.lastEval || (Date.now() - userData.lastEval > quinceDias);

  if (necesitaEval) {
    return <EvaluacionInicial uid={auth.currentUser?.uid} isVoluntario={false} />;
  }
  // --- FIN DEL PORTERO ---

  const cA = "#1E3A8A", cR = "#B91C1C", cV = "#047857";
const btnSt = { width: '100%', padding: '30px 10px', backgroundColor: '#ffffff', border: '2px solid #E2E8F0', borderRadius: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.03)' };

  const guardarCita = async () => {
    if(auth.currentUser) {
      await updateDoc(doc(db, "users", auth.currentUser.uid), { proximaCita: cita });
      setUserData({...userData, proximaCita: cita});
      alert("Cita guardada.");
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <GlobalStyles />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px 10px' }}>
        <div style={{ maxWidth: '800px', width: '100%', backgroundColor: '#ffffff', padding: '20px 15px', borderRadius: '32px', boxShadow: '0 15px 30px rgba(0,0,0,0.05)' }}>
          
          {userData?.admin && <button onClick={() => navigate('/admin')} className="admin-btn">⚙️ PANEL DE ADMINISTRADOR</button>}

          <h2 style={{ fontFamily: "'Montserrat', sans-serif", color: cA, fontSize: '40px', margin: '0 0 5px 0', fontWeight: '800', textAlign: 'center', letterSpacing: '2px' }}>BELTRAN PSICO</h2>
          <p style={{ color: '#64748B', fontSize: '15px', marginBottom: '25px', textAlign: 'center', lineHeight: '1.4' }}>
            <strong style={{ color: '#1E293B' }}>Psicólogo Francisco Beltrán</strong><br />Elche - online
          </p>

          <TarjetaFraseDia uid={auth.currentUser?.uid} userData={userData} />

          <div className="cita-box">
            <h3 style={{ color: '#4338CA', marginTop: 0, fontSize: '18px' }}>🗓️ Mi Próxima Sesión</h3>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
              <input type="datetime-local" value={cita} onChange={e=>setCita(e.target.value)} style={{ width: 'auto', fontSize: '14px', padding: '8px' }} />
              <button onClick={guardarCita} style={{ backgroundColor: '#4338CA', color: 'white', padding: '10px 15px', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px' }}>Guardar</button>
            </div>
          </div>

<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' }}>
            <button onClick={() => navigate('/mi-rincon')} style={{ ...btnSt, border: `2px solid ${cA}33` }}>
              <IconPersona color={cA} size="60" />
              <span style={{ fontWeight: '900', fontSize: '18px', color: cA }}>ACCESO<br/>INDIVIDUAL</span>
            </button>
            <button onClick={() => navigate('/nuestro-espacio')} style={{ ...btnSt, border: `2px solid ${cR}33` }}>
              <div style={{ display: 'flex', gap: '5px' }}><IconPersona color={cR} size="40" /><IconPersona color={cR} size="40" /></div>
              <span style={{ fontWeight: '900', fontSize: '18px', color: cR }}>CUIDANDO<br/>EL NOSOTROS</span>
            </button>
            <button onClick={() => navigate('/espacio-familiar')} style={{ ...btnSt, border: `2px solid ${cV}33` }}>
              <IconFamilia color={cV} size="60" />
              <span style={{ fontWeight: '900', fontSize: '18px', color: cV }}>CUIDANDO<br/>LA FAMILIA</span>
            </button>
          </div>
        </div>
      </div>
      <FooterFirma />
    </div>
  );
}

// ----------------- COMPONENTE PRINCIPAL (RUTAS) -----------------
export default function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const ref = doc(db, 'users', u.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) setUserData(snap.data());
        else {
          const newData = { nexoCode: Math.random().toString(36).substring(2, 7).toUpperCase(), partnerUid: null, familyGroupId: null, admin: false };
          await setDoc(ref, newData); setUserData(newData);
        }
      } else { setUserData(null); }
      setLoading(false);
    });
    return unsub;
  }, []);

  if (loading) return <div style={{ textAlign: 'center', marginTop: '100px', fontSize: '20px' }}>Cargando Beltran Psico...</div>;

  const sharedIdPar = userData?.partnerUid ? [user.uid, userData.partnerUid].sort().join('_') : (user ? `solo_par_${user.uid}` : null);
  const sharedIdFam = userData?.familyGroupId || (user ? `solo_fam_${user.uid}` : null);

  return (
    <div translate="no">
      <GlobalStyles />
      <BrowserRouter>
        <Routes>
          {!user ? ( <Route path="*" element={<AuthScreen />} /> ) : (
            <>
              <Route path="/" element={<Inicio userData={userData} setUserData={setUserData} />} />
              <Route path="/normas" element={<NormasUso />} />
              {userData?.admin && (
                <>
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/admin/paciente/:id/:nombre" element={<FichaPacienteAdmin />} />
                </>
              )}
              
              <Route path="/mi-rincon" element={<MiRincon />} />
              <Route path="/evaluacion-quincenal" element={<EvaluacionInicial uid={user.uid} isVoluntario={true} />} />
              <Route path="/recordatorios-individual" element={<TablonRecordatorios docUid={user.uid} color="#1E3A8A" collectionName="recordatorios_ind" titulo="TABLÓN PRIVADO" />} />
              <Route path="/mi-autoestima" element={<MotorAutoestima titulo="MI AUTOESTIMA" color="#1E3A8A" bg="#F8FAFC" />} />
              <Route path="/mis-valores" element={<HerramientaBase titulo="MIS VALORES" color="#1E3A8A" bg="#F8FAFC" storageKey="val_ind" placeholder="Ej: Mantener la honestidad en el trabajo..." explicacion="Los valores son tu brújula vital, los principios que guían tus decisiones y dan sentido a lo que haces. Escribe aquí qué es verdaderamente importante e innegociable para ti en la vida (ej: la lealtad, la tranquilidad, el crecimiento personal, la honestidad)." numerado={true} accionEspecial="objetivo" collectionObjetivos="obj_ind" />} />
              <Route path="/objetivos-individuales" element={<MotorObjetivos titulo="MIS OBJETIVOS Y CONDUCTAS" color="#1E3A8A" colorFondo="#F8FAFC" storageKey="obj_ind" collectionAlerts="recordatorios_ind" placeholderObj="Ej: Aprender a ser más asertivo..." placeholderConducta="Ej: Decir 'no' a peticiones que no me apetecen..." explicacion="Los objetivos pueden ser ideas generales o específicas. Las conductas siempre tienen que ser acciones muy bien descritas y concretas escritas en infinitivo. <strong>Dale a la campana (🔔) para enviarlo al tablón.</strong>" />} />
              <Route path="/resumen-individual" element={<MotorAprendizajes titulo="MI RESUMEN" color="#1E3A8A" bg="#F8FAFC" storageKey="res_ind" collectionAlerts="recordatorios_ind" showUsageTracker={true} placeholder="Ej: He aprendido a identificar cuando me pongo a la defensiva..." explicacion="Anota aquí las ideas clave, conclusiones o esos 'clics' mentales que vas descubriendo. Utiliza los botones inferiores para evaluar con qué frecuencia estás logrando aplicarlo." textosSemaforo={['Casi siempre', 'A veces', 'Poco']} />} />
              <Route path="/procesos-pec-individual" element={<MotorProcesosPEC titulo="MIS PROCESOS P-E-C" color="#1E3A8A" bg="#F8FAFC" storageKey="pec_ind" collectionAlerts="recordatorios_ind" />} />
              <Route path="/baul-individual" element={<HerramientaBase titulo="EL BAÚL DE ENREDOS" color="#1E3A8A" bg="#F8FAFC" storageKey="b_ind" placeholder="Ej: Me preocupa mucho el examen del viernes..." explicacion="Este es un espacio de 'descarga'. Escribe aquí todos esos pensamientos recurrentes o preocupaciones que no te dejan avanzar. Es como tomarte un 'Kit-Kat' mental para retomarlos posteriormente." />} />
              <Route path="/luz-individual" element={<MotorAprendizajes titulo="HACIENDO MI MUNDO BONITO" color="#1E3A8A" bg="#F8FAFC" storageKey="l_ind" collectionAlerts="recordatorios_ind" placeholder="Ej: Salir a caminar 30 minutos..." explicacion="A veces, nos olvidamos de esas pequeñas cosas que nos hacen sentir bien. Este espacio es para proponer acciones sencillas y agradables.<br/>Escribe en infinitivo. Usa la campana (🔔) para enviarlo a tu Tablón." textosSemaforo={['A menudo', 'A veces', 'Muy poco']} />} />
              
{/* CUIDANDO EL NOSOTROS (PAREJAS) */}
              <Route path="/nuestro-espacio" element={<NuestroEspacio userData={userData} setUserData={setUserData} sharedId={sharedIdPar} />} />
              {sharedIdPar && (
                <>
                  <Route path="/recordatorios-pareja" element={<TablonRecordatorios docUid={sharedIdPar} color="#B91C1C" collectionName="recordatorios_par" titulo="TABLÓN COMPARTIDO" />} />
                  <Route path="/moviendonos-pareja" element={<MoviendonosPareja docUid={sharedIdPar} userData={userData} />} />
                  <Route path="/fortalezas-pareja" element={<HerramientaBase docUid={sharedIdPar} titulo="NUESTRAS FORTALEZAS" color="#B91C1C" bg="#FEF2F2" storageKey="f_par" placeholder="Ej: Nuestra capacidad para reírnos de los problemas..." explicacion="Escribid aquí los puntos fuertes de vuestra relación y los recursos internos con los que contáis como equipo." />} />
                </>
              )}

              {/* CUIDANDO LA FAMILIA */}
              <Route path="/espacio-familiar" element={<EspacioFamiliar userData={userData} setUserData={setUserData} familyId={sharedIdFam} />} />
              {sharedIdFam && (
                <>
                  <Route path="/recordatorios-familia" element={<TablonRecordatorios docUid={sharedIdFam} color="#047857" collectionName="recordatorios_fam" titulo="TABLÓN FAMILIAR" />} />
                  <Route path="/objetivos-familia" element={<MotorObjetivos docUid={sharedIdFam} titulo="NUESTROS OBJETIVOS" color="#047857" colorFondo="#ECFDF5" storageKey="obj_fam" collectionAlerts="recordatorios_fam" placeholderObj="Ej: Repartir mejor las tareas de casa..." placeholderConducta="Ej: Hacer la compra entre los dos..." explicacion="Formulad las acciones como un equipo familiar. <strong>Usad la campana (🔔) para recordatorios.</strong>" />} />
                  <Route path="/fortalezas-familia" element={<HerramientaBase docUid={sharedIdFam} titulo="NUESTRAS FORTALEZAS" color="#047857" bg="#ECFDF5" storageKey="f_fam" placeholder="Ej: Apoyarnos cuando alguien tiene un mal día..." explicacion="Escribid aquí los puntos fuertes de vuestra familia y lo que hacéis bien como equipo." />} />
                  <Route path="/procesos-pec-familia" element={<MotorProcesosPEC docUid={sharedIdFam} titulo="P-E-C FAMILIAR" color="#047857" bg="#ECFDF5" storageKey="pec_fam" collectionAlerts="recordatorios_fam" />} />
                  <Route path="/resumen-familia" element={<MotorAprendizajes docUid={sharedIdFam} titulo="RESUMEN DE SESIONES" color="#047857" bg="#ECFDF5" storageKey="r_fam" collectionAlerts="recordatorios_fam" showUsageTracker={true} placeholder="Ej: Aprender a escuchar sin juzgar al otro..." explicacion="Anotad aquí los acuerdos terapéuticos familiares y evaluad con qué frecuencia los aplicáis." textosSemaforo={['Casi siempre', 'A veces', 'Poco']} />} />
                  <Route path="/baul-familia" element={<HerramientaBase docUid={sharedIdFam} titulo="EL BAÚL DE LOS ENREDOS" color="#047857" bg="#ECFDF5" storageKey="b_fam" placeholder="Ej: Preocupación por las notas del instituto..." explicacion="Espacio de 'descarga' familiar. Escribid aquí las preocupaciones conjuntas que os bloquean." />} />
                  <Route path="/luz-familia" element={<MotorAprendizajes docUid={sharedIdFam} titulo="CUIDANDO LA FAMILIA" color="#047857" bg="#ECFDF5" storageKey="l_fam" collectionAlerts="recordatorios_fam" placeholder="Ej: Hacer una excursión el domingo..." explicacion="Proponed planes positivos y agradables para la familia. Escribid las acciones en infinitivo." textosSemaforo={['A menudo', 'A veces', 'Muy poco']} />} />
                </>
              )}
            </>
          )}
        </Routes>
      </BrowserRouter>
    </div>
  );
}